import React, { useState, useEffect, useRef, useMemo, ChangeEvent, use } from "react";
import * as Papa from 'papaparse';
import { useAddQuestionBankToQuiz, useAddQuestionToBank, useCreateQuestion, useCreateQuestionBank, useOverallVideoAnalytics, userParseCSVtoItems, useUpdateItemOptional, useUpdateItemProctoring, useUpdateModuleProctoring, useVideoUserAnalytics } from '@/hooks/hooks';
import { BarChart3, Download, LogOut, Upload, UserRoundCheck, Video, Clock, PlayCircle, Users, Search, LockOpen, Lock } from 'lucide-react';
import { useHideItem, exportCourseQuestionBank } from '@/hooks/hooks';
import { DetectorChecklist, allDetectorsOff, type DetectorSetting } from '@/components/proctoring-detectors';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

const MAX_DESCRIPTION_LENGTH = 1000;

import {
  Sidebar, SidebarHeader, SidebarContent, SidebarMenu, SidebarMenuItem,
  SidebarMenuButton, SidebarMenuSub, SidebarMenuSubItem, SidebarMenuSubButton,
  SidebarInset, SidebarProvider, SidebarFooter, useSidebar,
  SidebarTrigger
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Reorder } from "motion/react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/theme-toggle";
import {
  BookOpen, ChevronRight, FileText, VideoIcon, ListChecks, Plus, Sparkles,
  X, FolderKanban,
  Menu,
  MessageSquare,
  NotebookPen,
  Eye,
  EyeOff,
  Loader2,
  ArrowUp,
  ArrowDown,
  Pencil,
  MessageSquareQuote,
  Scale,
} from "lucide-react";

import { useNavigate } from "@tanstack/react-router";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Home, GraduationCap } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { useCourseVersionById, useCreateModule, useUpdateModule, useDeleteModule, useCreateSection, useUpdateSection, useDeleteSection, useCreateItem, useUpdateItem, useDeleteItem, useItemsBySectionId, useItemById, useQuizDetails, useQuizAnalytics, useQuizPerformance, useQuizResults, useMoveModule, useMoveSection, useMoveItem, useUpdateCourseItem, useCourseById, useHideModule, useHideSection } from "@/hooks/hooks";
import { useCourseStore } from "@/store/course-store";
import VideoModal from "./components/Video-modal";
import StudentQuestionPanel from "./components/StudentQuestionPanel";
import EnhancedQuizEditor from "./components/enhanced-quiz-editor";
import EnhancedBlogEditor from "./components/enhanced-blog-editor";
import QuizWizardModal from "./components/quiz-wizard";
import { useAuthStore } from "@/store/auth-store";
import { toast } from "sonner";
import Loader from "@/components/Loader";
import { Label } from "@/components/ui/label";
import ProjectItem from "./components/ProjectItem";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup, SidebarResizablePanel } from "@/components/ui/resizable";
import FeedbackFormEditor from "./FeedbackFormEditor";
import ReflectionItemEditor from './components/ReflectionItemEditor';
import CaseStudyItemEditor from './components/CaseStudyItemEditor';
import CaseStudyResponsesPanel from './components/CaseStudyResponsesPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/utils/utils";
import { QuestionUploadDialog } from "@/components/question-upload-dialog";
import ConfirmationModal from "./components/confirmation-modal";
import { useMatches, Link } from "@tanstack/react-router";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { BreadcrumbItemment } from "@/types/layout.types";
import AiWorkflow from "./AiWorkflow";
import AISectionPage from "./AISectionPage";
import SmartBloomWorkflow from "./SmartBloomWorkflow";
type Mode = "default" | "wizard" | "smartBloom" | "custom" | "ai-module" | "advanced";
import { logout } from "@/utils/auth";
import InviteDropdown from "@/components/inviteDropDown";
import AiModule from "./AiModule";
import AdvancedAiWorkflow from "./AdvancedAiWorkflow";
import { useQueryClient } from "@tanstack/react-query"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Pagination } from "@/components/ui/Pagination";
import CourseBackButton from "./CourseBackButton";


// ? Icons per item type
const getItemIcon = (type: string) => {
  switch (type) {
    case "BLOG": return <FileText className="h-3 w-3" />;
    case "VIDEO": return <VideoIcon className="h-3 w-3" />;
    case "QUIZ": return <ListChecks className="h-3 w-3" />;
    case "PROJECT": return <FolderKanban className="h-3 w-3" />;
    case "FEEDBACK": return <MessageSquare className="h-3 w-3" />;
    case "REFLECTION": return <NotebookPen className="h-3 w-3" />;
    case "CASE_STUDY": return <Scale className="h-3 w-3" />;
    default: return null;
  }
};

// Items (video/quiz/blog) don't carry their own createdAt field, but their
// Mongo ObjectId does: the first 4 bytes are a big-endian Unix timestamp set
// at generation time, and item _ids are generated once, at creation.
function getCreatedAtFromObjectId(id?: string | null): Date | null {
  if (!id || id.length < 8) return null;
  const seconds = parseInt(id.substring(0, 8), 16);
  return Number.isNaN(seconds) ? null : new Date(seconds * 1000);
}

interface LabelOptions {
  itemId: string;
  itemType: "VIDEO" | "QUIZ" | "BLOG" | "PROJECT" | "FEEDBACK" | "REFLECTION";
  sectionItems: Record<string, any[]>;
  sectionId: string;
}

interface ModuleData {
  name: string;
  description: string;
}

// Interface for CSV row
type CSVRow = {
  'yotube url'?: string;
  'Segment'?: string;
  'Question Timestamp [mm:ss]'?: string;
  'S.No.'?: string;
  'Question'?: string;
  'Hint'?: string;
  'Option A'?: string;
  'Expln-A'?: string;
  'Option B'?: string;
  'Expln-B'?: string;
  'Option C'?: string;
  'Expln-C'?: string;
  'Option D'?: string;
  'Expln-D'?: string;
  'Correct Answer'?: string;
  [key: string]: string | undefined;
};

function TeacherCourseContent() {
  const [mode, setMode] = useState<Mode>("default");
  const matches = useMatches();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [showInvites, setShowInvites] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const invitesRef = useRef<HTMLDivElement | null>(null);
  const [videoTab, setVideoTab] = useState("video");
  const [caseStudyTab, setCaseStudyTab] = useState("settings");
  const [isReorderEnabled, setIsReorderEnabled] = useState(false);
  const [isExportingQuestionBank, setIsExportingQuestionBank] = useState(false);



  const handleLogout = () => {
    logout();
    navigate({ to: "/auth" });
  };
  const createQuestion = useCreateQuestion();
  const createQuestionBank = useCreateQuestionBank();
  const addQuestionBankToQuiz = useAddQuestionBankToQuiz();
  const user = useAuthStore().user;
  const { currentCourse, setCurrentCourse } = useCourseStore();
  // Use correct keys for course/version IDs
  const courseId = currentCourse?.courseId;
  const versionId = currentCourse?.versionId;

  const handleExportQuestionBank = async () => {
    if (!courseId || !versionId) return;
    setIsExportingQuestionBank(true);
    try {
      await exportCourseQuestionBank(courseId, versionId);
      toast.success("Question bank exported");
    } catch (error: any) {
      toast.error(error?.message || "Failed to export question bank");
    } finally {
      setIsExportingQuestionBank(false);
    }
  };





  useEffect(() => {
    const items: BreadcrumbItem[] = [];
    items.push({
      label: "Dashboard",
      path: "/teacher",
      isCurrentPage: matches.length === 1,
    });
    items.push({
      label: "Teacher",
      path: "/teacher",
      isCurrentPage: matches.length === 1,
    });
    if (matches.length > 1) {
      for (let i = 1; i < matches.length; i++) {
        const match = matches[i];
        const path = match.pathname;
        const segments = path.split("/").filter(Boolean);
        let label = segments[segments.length - 1] || "";
        label = label.replace(/-/g, " ");
        label = label.charAt(0).toUpperCase() + label.slice(1);

        items.push({
          label,
          path,
          isCurrentPage: i === matches.length - 1,
        });
      }
    }

    setBreadcrumbs(items);
  }, [matches]);
  // const { setOpen, setOpenMobile } = useSidebar();
  // const [isDesktopSidebarVisible, setIsDesktopSidebarVisible] = useState(true);

  const checkScreenSize = () => {
    return window.innerWidth <= 425;
  };

  // useEffect(() => {
  //   const handleResize = () => {
  //     const width = window.innerWidth;
  //     if (width >= 768) {
  //       setOpen(true);
  //     }
  //   };

  //   window.addEventListener('resize', handleResize);
  //   handleResize();

  //   return () => window.removeEventListener('resize', handleResize);
  // }, [setOpen]);

  // Fetch course version data (modules, sections, items)
  const { data: versionData, refetch: refetchVersion, isLoading } = useCourseVersionById(versionId || "");

  // fetch course data
  const { data: courseData } = useCourseById(courseId || "")

  // Some APIs return modules directly, some wrap in 'version'. Try both.
  // @ts-ignore
  const modules = (versionData as any)?.modules || (versionData as any)?.version?.modules || [];

  const [initialModules, setInitialModules] = useState<typeof modules[]>(modules);
  // Animated text for empty state
  const aiMessages = [
    "ViBe allows you to add sections in your course module using AI",
    "Generate engaging content with AI-powered tools",
    "Create quizzes and assessments with intelligent assistance",
    "Transform your teaching with AI-enhanced course creation"
  ];
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayedMessage, setDisplayedMessage] = useState(aiMessages[0]);
  const [isVisible, setIsVisible] = useState(true);
  const [selectedItem, setSelectedItem] = useState({ id: "", name: "" });

  // State for project modal
  const [showAddProjectModal, setShowAddProjectModal] = useState<{
    moduleId: string;
    sectionId: string;
  } | null>(null);

  const [errors, setErrors] = useState({
    title: "",
    description: "",
  });

  const [isEditingModule, setIsEditingModule] = useState(false);
  const [isEditingSection, setIsEditingSection] = useState(false);
  const [originalModuleData, setOriginalModuleData] = useState<ModuleData | null>(null);
  const [originalSectionData, setOriginalSectionData] = useState<{ name: string; description: string } | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);


  // const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [hidingModuleId, setHidingModuleId] = useState<string | null>(null);
  const [hidingSectionId, setHidingSectionId] = useState<string | null>(null);
  const [hidingItemId, setHidingItemId] = useState<string | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false); // fade out
      setTimeout(() => {
        const nextIndex = (currentTextIndex + 1) % aiMessages.length;
        setCurrentTextIndex(nextIndex);
        setDisplayedMessage(aiMessages[nextIndex]);
        setIsVisible(true); // fade in
      }, 400); // fade out duration
    }, 4000); // Change text every 4 seconds

    return () => clearInterval(interval);
  }, [currentTextIndex, aiMessages]);

  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [autoSelectSectionsToLoad, setAutoSelectSectionsToLoad] = useState<Array<{ moduleId: string, sectionId: string }>>([]);
  const [autoSelectCurrentIndex, setAutoSelectCurrentIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [selectedEntity, setSelectedEntity] = useState<{
    type: "module" | "section" | "item";
    data: any;
    parentIds?: { moduleId: string; sectionId?: string; itemsGroupId?: string };
  } | null>(null);
  const [isEditingItem, setIsEditingItem] = useState(false);
  const [studentQuestionSegment, setStudentQuestionSegment] = useState<{
    segmentId: string;
    segmentTitle?: string;
  } | null>(null);

  // Add this state for the add video modal
  const [showAddVideoModal, setShowAddVideoModal] = useState<{
    moduleId: string;
    sectionId: string;
  } | null>(null);

  // Add this state for the quiz wizard modal
  const [quizWizardOpen, setQuizWizardOpen] = useState(false);
  const [quizModuleId, setQuizModuleId] = useState<string>("");
  const [quizSectionId, setQuizSectionId] = useState<string>("");

  // Store items for each section
  const [sectionItems, setSectionItems] = useState<Record<string, any[]>>({});

  const [togglingItemId, setTogglingItemId] = useState<string | null>(null);
  const [togglingItemProctoringId, setTogglingItemProctoringId] = useState<string | null>(null);
  const [togglingModuleProctoringId, setTogglingModuleProctoringId] = useState<string | null>(null);
  // Optimistic value shown the instant a proctoring toggle/checklist is
  // clicked, before the save round-trip lands -- cleared on settle (success
  // or failure), which snaps the UI back to real server data either way.
  const [optimisticItemDetectors, setOptimisticItemDetectors] = useState<{
    itemId: string;
    detectors: DetectorSetting[] | null;
  } | null>(null);
  const [optimisticModuleDetectors, setOptimisticModuleDetectors] = useState<{
    moduleId: string;
    detectors: DetectorSetting[] | null;
  } | null>(null);
  // Re-entrancy guards for the two save functions below: the `isBusy` flags
  // that disable the checklist UI only take effect once React commits the
  // re-render from setTogglingItem/ModuleProctoringId, one tick after the
  // click that set them. A second checkbox click landing inside that window
  // still reads the pre-toggle `value` prop (neither save has resolved yet),
  // computes its own array without the first click's change, and whichever
  // save's PUT lands second on the server wins -- silently discarding the
  // first click. A plain ref is synchronous, so checking it before any
  // `await` closes that window regardless of render timing.
  const itemProctoringSaveInFlight = useRef(false);
  const moduleProctoringSaveInFlight = useRef(false);
  // Latest detector array queued by a click that landed while a save was
  // already in flight. `undefined` means "nothing queued" -- distinct from
  // `null`, which is itself a valid detectors value (clearing an override).
  // The in-flight save's own loop drains this once it finishes, so a burst
  // of rapid clicks all eventually land instead of being silently dropped
  // once the in-flight guard above blocks their own save attempt.
  const itemProctoringPending = useRef<DetectorSetting[] | null | undefined>(undefined);
  const moduleProctoringPending = useRef<DetectorSetting[] | null | undefined>(undefined);

  // Check if a project already exists in any section
  const hasExistingProject = useMemo(() => {
    return Object.values(sectionItems).some(items =>
      items.some(item => item.type === 'PROJECT')
    );
  }, [sectionItems]);

  // Controlled state for ProjectItem edit mode
  const [projectEditName, setProjectEditName] = useState<string>('');
  const [projectEditDescription, setProjectEditDescription] = useState<string>('');

  // Track which section to fetch items for
  const [activeSectionInfo, setActiveSectionInfo] = useState<{ moduleId: string; sectionId: string } | null>(null);

  // Fetch items for the active section
  const shouldFetchItems = !!(activeSectionInfo?.moduleId && activeSectionInfo?.sectionId && versionId);
  const safeVersionId = versionId && versionId.trim() ? versionId : "SKIP";
  const safeModuleId = activeSectionInfo?.moduleId && activeSectionInfo.moduleId.trim() ? activeSectionInfo.moduleId : "SKIP";
  const safeSectionId = activeSectionInfo?.sectionId && activeSectionInfo.sectionId.trim() ? activeSectionInfo.sectionId : "SKIP";

  const {
    data: currentSectionItems,
    isLoading: itemsLoading,
    refetch: refetchItems
  } = useItemsBySectionId(
    safeVersionId,
    safeModuleId,
    safeSectionId
  );

  // Fetch item details for selected item
  const shouldFetchItem = selectedEntity?.type === 'item' && !!courseId && !!versionId && !!selectedEntity?.data?._id && !!activeSectionInfo?.moduleId && !!activeSectionInfo?.sectionId;
  const {
    data: selectedItemData,
    isLoading: isItemLoading,
    refetch: refetchItem
  } = useItemById(
    shouldFetchItem ? courseId : '',
    shouldFetchItem ? versionId : '',
    shouldFetchItem ? selectedEntity?.data?._id : '',
    shouldFetchItem ? activeSectionInfo!.moduleId : '',
    shouldFetchItem ? activeSectionInfo!.sectionId : '',
  );

  const [videoAnalyticsPage, setVideoAnalyticsPage] = useState(1);
  const [videoAnalyticsLimit, setVideoAnalyticsLimit] = useState(12);
  const [videoAnalyticsSearch, setVideoAnalyticsSearch] = useState("");
  const [debouncedVideoAnalyticsSearch, setDebouncedVideoAnalyticsSearch] = useState("");
  const [videoAnalyticsSortBy, setVideoAnalyticsSortBy] = useState<'name' | 'views' | 'watchHours'>('name');
  const [videoAnalyticsSortOrder, setVideoAnalyticsSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedVideoAnalyticsSearch(videoAnalyticsSearch);
      setVideoAnalyticsPage(1);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [videoAnalyticsSearch]);

  const {
    data: overallAnalytics,
    isLoading: overallLoading,
    error: overallError,
    refetch: refetchOverall,
  } = useOverallVideoAnalytics(
    courseId!, 
    versionId!, 
    selectedEntity?.data?.type === 'VIDEO' ? selectedEntity?.data?._id : ''
  );

  const videoUserAnalyticsQuery = useVideoUserAnalytics(
    courseId!,
    versionId!,
    selectedEntity?.data?.type === 'VIDEO' ? selectedEntity?.data?._id : '',
    {
      page: videoAnalyticsPage,
      limit: videoAnalyticsLimit,
      search: debouncedVideoAnalyticsSearch,
      sortBy: videoAnalyticsSortBy,
      sortOrder: videoAnalyticsSortOrder,
    }
  );

  const {
    data: userAnalyticsData,
    totalDocuments: userAnalyticsTotalDocs,
    totalPages: userAnalyticsTotalPages,
    page,
    limit,
  } = videoUserAnalyticsQuery.data ?? {};

  const {
    isLoading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = videoUserAnalyticsQuery;

  // Sync controlled state with selectedItemData for PROJECT edit
  useEffect(() => {
    if (selectedEntity?.type === 'item' && selectedEntity.data.type === 'PROJECT') {
      setProjectEditName(selectedItemData?.item?.name || '');
      setProjectEditDescription(selectedItemData?.item?.description || '');
    }
  }, [selectedEntity, selectedItemData]);

  const selectedQuizId = selectedEntity?.type === 'item' && selectedEntity?.data?.type === 'QUIZ' ? selectedEntity.data._id : null;

  const { data: quizDetails } = useQuizDetails(selectedQuizId);
  const { data: quizAnalytics } = useQuizAnalytics(selectedQuizId);
  // const { data: quizSubmissions } = useQuizSubmissions(selectedQuizId, selectedGradeStatus, sort, currentPage, limit);
  const { data: quizPerformance } = useQuizPerformance(selectedQuizId);

  const toggleModule = (moduleId: string) => {
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const toggleSection = (moduleId: string, sectionId: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
    setActiveSectionInfo({ moduleId, sectionId });
  };

  // CRUD hooks

  // --- MODULES ---
  const { mutateAsync: createModuleAsync, isSuccess: isCreateModuleSuccess, isError: isCreateModuleError, error: createModuleError, } = useCreateModule();
  const { mutateAsync: updateModuleAsync, isSuccess: isUpdateModuleSuccess, isError: isUpdateModuleError, error: updateModuleError } = useUpdateModule();
  const { mutateAsync: deleteModuleAsync, isSuccess: isDeleteModuleSuccess, isError: isDeleteModuleError, error: deleteModuleError } = useDeleteModule();
  const { mutateAsync: moveModuleAsync } = useMoveModule();
  const { mutateAsync: hideModuleAsync } = useHideModule();

  // --- SECTIONS ---
  const { mutateAsync: createSectionAsync, isSuccess: isCreateSectionSuccess, isError: isCreateSectionError, error: createSectionError } = useCreateSection();
  const { mutateAsync: updateSectionAsync, isSuccess: isUpdateSectionSuccess, isError: isUpdateSectionError, error: updateSectionError } = useUpdateSection();
  const { mutateAsync: deleteSectionAsync, isSuccess: isDeleteSectionSuccess, isError: isDeleteSectionError, error: deleteSectionError } = useDeleteSection();
  const { mutateAsync: moveSectionAsync } = useMoveSection();
  const { mutateAsync: hideSectionAsync } = useHideSection();

  // --- ITEMS ---
  const { mutateAsync: createItemAsync, isSuccess: isCreateItemSuccess, isError: isCreateItemError, error: createItemError } = useCreateItem();
  const { mutateAsync: updateItemAsync, isSuccess: isUpdateItemSuccess, isError: isUpdateItemError, error: updateItemError } = useUpdateItem();
  const { mutateAsync: updateCourseItemAsync } = useUpdateCourseItem();
  const { mutateAsync: updateVideoAsync } = useUpdateCourseItem();
  const { mutateAsync: deleteItemAsync, isSuccess: isDeleteItemSuccess, isError: isDeleteItemError, error: deleteItemError } = useDeleteItem();
  const { mutateAsync: moveItemAsync, isPending, isError: isMoveItemError, error: moveItemError } = useMoveItem();
  const { mutateAsync: updateItemVisibilityAsync } = useHideItem();

  const [isProcessingCSV, setIsProcessingCSV] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const userCSVtoItem = userParseCSVtoItems();
  const queryClient = useQueryClient()

  // Quiz creation flow: after picking "Quiz" from the Add Item dropdown the user
  // chooses between starting blank or uploading a CSV of questions.
  const [quizChoiceOpen, setQuizChoiceOpen] = useState(false);
  const [pendingQuizContext, setPendingQuizContext] = useState<{
    moduleId: string;
    sectionId: string;
  } | null>(null);
  const [quizCsvDialogOpen, setQuizCsvDialogOpen] = useState(false);


  const updateItemOptional = useUpdateItemOptional();
  const updateItemProctoring = useUpdateItemProctoring();
  const updateModuleProctoring = useUpdateModuleProctoring();

  // Refetch after any success
  useEffect(() => {
    if (
      isCreateModuleSuccess ||
      isUpdateModuleSuccess ||
      isDeleteModuleSuccess ||
      isCreateSectionSuccess ||
      isUpdateSectionSuccess ||
      isDeleteSectionSuccess ||
      isCreateItemSuccess ||
      isUpdateItemSuccess ||
      isDeleteItemSuccess
    ) {
      refetchVersion();
      if (shouldFetchItems) {
        refetchItems();
      }

      if (activeSectionInfo) {
        setActiveSectionInfo({ ...activeSectionInfo }); // triggers refetch
      }
    }
  }, [
    isCreateModuleSuccess,
    isUpdateModuleSuccess,
    isDeleteModuleSuccess,
    isCreateSectionSuccess,
    isUpdateSectionSuccess,
    isDeleteSectionSuccess,
    isCreateItemSuccess,
    isUpdateItemSuccess,
    isDeleteItemSuccess,
  ]);

  useStatusToasts({
    successFlags: {
      isCreateModuleSuccess: {
        flag: isCreateModuleSuccess,
        message: "Module created successfully!",
      },
      isUpdateModuleSuccess: {
        flag: isUpdateModuleSuccess,
        message: "Module updated successfully!",
      },
      isDeleteModuleSuccess: {
        flag: isDeleteModuleSuccess,
        message: "Module deleted successfully!",
      },
      isCreateSectionSuccess: {
        flag: isCreateSectionSuccess,
        message: "Section created successfully!",
      },
      isUpdateSectionSuccess: {
        flag: isUpdateSectionSuccess,
        message: "Section updated successfully!",
      },
      isDeleteSectionSuccess: {
        flag: isDeleteSectionSuccess,
        message: "Section deleted successfully!",
      },
      isCreateItemSuccess: {
        flag: isCreateItemSuccess,
        message: "Item created successfully!",
      },
      isUpdateItemSuccess: {
        flag: isUpdateItemSuccess,
        message: "Item updated successfully!",
      },
      isDeleteItemSuccess: {
        flag: isDeleteItemSuccess,
        message: "Item deleted successfully!",
      },
    },
    errorFlags: {
      // isCreateModuleError: {
      //   flag: isCreateModuleError,
      //   message: createModuleError?.response?.data?.message || createModuleError?.message,
      //   fallback: "Failed to create module",
      // },
      isUpdateModuleError: {
        flag: isUpdateModuleError,
        message: updateModuleError?.toString(),
        fallback: "Failed to update module",
      },
      isDeleteModuleError: {
        flag: isDeleteModuleError,
        message: deleteModuleError?.toString(),
        fallback: "Failed to delete module",
      },
      isCreateSectionError: {
        flag: isCreateSectionError,
        message: createSectionError?.toString(),
        fallback: "Failed to create section",
      },
      isUpdateSectionError: {
        flag: isUpdateSectionError,
        message: updateSectionError?.toString(),
        fallback: "Failed to update section",
      },
      isDeleteSectionError: {
        flag: isDeleteSectionError,
        message: deleteSectionError?.toString(),
        fallback: "Failed to delete section",
      },
      isCreateItemError: {
        flag: isCreateItemError,
        message: createItemError?.toString(),
        fallback: "Failed to create item",
      },
      isUpdateItemError: {
        flag: isUpdateItemError,
        message: updateItemError?.toString(),
        fallback: "Failed to update item",
      },
      isDeleteItemError: {
        flag: isDeleteItemError,
        message: deleteItemError?.toString(),
        fallback: "Failed to delete item",
      },
      isMoveItemError: {
        flag: isMoveItemError,
        message: moveItemError?.toString(),
        fallback: "Failed to move item",
      },
    },
  });


  // Reload items when quiz wizard closes
  useEffect(() => {
    if (!quizWizardOpen && quizModuleId && quizSectionId) {
      // Quiz wizard just closed, reload items for the section
      setActiveSectionInfo({ moduleId: quizModuleId, sectionId: quizSectionId });
      refetchVersion();
      if (shouldFetchItems) {
        refetchItems();
      }
    }
  }, [quizWizardOpen, quizModuleId, quizSectionId, refetchVersion, shouldFetchItems]);

  // Update sectionItems state when items are fetched
  useEffect(() => {
    if (
      shouldFetchItems &&
      activeSectionInfo?.sectionId &&
      currentSectionItems &&
      !itemsLoading
    ) {

      const itemsArray = (currentSectionItems as any)?.items || (Array.isArray(currentSectionItems) ? currentSectionItems : []);


      setSectionItems(prev => ({
        ...prev,
        [activeSectionInfo.sectionId]: itemsArray
      }));

      // If we're in auto-select mode and have a watchItemId, check if the target item is in this newly loaded section
      const watchItemId = currentCourse?.watchItemId;


      if (watchItemId && itemsArray.length > 0) {

        const targetItem = itemsArray.find((item: any) => item._id === watchItemId);
        if (targetItem) {


          // Find the module for this section
          const targetModule = modules?.find(module =>
            module.sections?.some(section => section.sectionId === activeSectionInfo.sectionId)
          );

          if (targetModule) {
            const targetSection = targetModule.sections?.find(section => section.sectionId === activeSectionInfo.sectionId);

            if (targetSection) {


              // Show toast notification for successful navigation
              const questionId = currentCourse?.questionId;
              if (questionId) {
                toast.success(`Navigated to flagged question in "${targetItem.name}"`);
              } else {
                toast.success(`Navigated to flagged item: "${targetItem.name}"`);
              }

              // Expand the module and section
              setExpandedModules(prev => ({ ...prev, [targetModule.moduleId]: true }));
              setExpandedSections(prev => ({ ...prev, [targetSection.sectionId]: true }));

              // Select the item
              setSelectedEntity({
                type: 'item',
                data: targetItem,
                parentIds: {
                  moduleId: targetModule.moduleId,
                  sectionId: targetSection.sectionId
                }
              });

              // Clear watchItemId and questionId after navigation
              setCurrentCourse({
                ...currentCourse,
                watchItemId: null,
                // questionId: null // Keep questionId for EnhancedQuizEditor to use
              });
              setAutoSelectSectionsToLoad([]);
              setAutoSelectCurrentIndex(0);


            } else {

            }
          } else {

          }
        } else {

        }
      }
    }
  }, [currentSectionItems, itemsLoading, activeSectionInfo, shouldFetchItems, currentCourse, modules]);

  // Auto-select item when navigating from flagged list
  useEffect(() => {
    const watchItemId = currentCourse?.watchItemId;
    const questionId = currentCourse?.questionId;

    // Wait for version data to load before attempting auto-select
    if (isLoading) {

      return;
    }

    if (!watchItemId || !modules || modules.length === 0) return;



    if (questionId) {

    }

    // First, try to find the item in already-loaded sectionItems
    for (const module of modules) {

      for (const section of module.sections || []) {

        const items = sectionItems[section.sectionId] || [];


        const targetItem = items.find((item: any) => item._id === watchItemId);

        if (targetItem) {


          // Show toast notification for successful navigation
          if (questionId) {
            toast.success(`Navigated to flagged question in "${targetItem.name}"`);
          } else {
            toast.success(`Navigated to flagged item: "${targetItem.name}"`);
          }

          // Expand the module and section
          setExpandedModules(prev => ({ ...prev, [module.moduleId]: true }));
          setExpandedSections(prev => ({ ...prev, [section.sectionId]: true }));

          // Select the item
          setSelectedEntity({
            type: 'item',
            data: targetItem,
            parentIds: {
              moduleId: module.moduleId,
              sectionId: section.sectionId
            }
          });

          // Clear watchItemId and questionId after navigation
          setCurrentCourse({
            ...currentCourse,
            watchItemId: null,
            // questionId: null // Keep questionId for EnhancedQuizEditor to use
          });
          setAutoSelectSectionsToLoad([]);
          setAutoSelectCurrentIndex(0);

          return;
        }
      }
    }

    // If not found and we haven't started loading sections yet, prepare the list
    if (autoSelectSectionsToLoad.length === 0) {


      const sectionsToLoad: Array<{ moduleId: string, sectionId: string }> = [];
      modules.forEach(module => {

        module.sections?.forEach(section => {

          sectionsToLoad.push({ moduleId: module.moduleId, sectionId: section.sectionId });
        });
      });


      setAutoSelectSectionsToLoad(sectionsToLoad);
      setAutoSelectCurrentIndex(0);

      // Expand all modules and sections
      const newExpandedModules: Record<string, boolean> = {};
      const newExpandedSections: Record<string, boolean> = {};
      modules.forEach(module => {
        newExpandedModules[module.moduleId] = true;
        module.sections?.forEach(section => {
          newExpandedSections[section.sectionId] = true;
        });
      });
      setExpandedModules(newExpandedModules);
      setExpandedSections(newExpandedSections);
    }
  }, [currentCourse?.watchItemId, modules, sectionItems, autoSelectSectionsToLoad.length, isLoading]);

  // Load sections one by one for auto-selection
  useEffect(() => {
    // If not in auto-select mode or no item to watch, do nothing
    if (autoSelectSectionsToLoad.length === 0 || !currentCourse?.watchItemId) return;

    const currentTarget = autoSelectSectionsToLoad[autoSelectCurrentIndex];

    // If we've run out of sections to check, stop
    if (!currentTarget) {
      setAutoSelectSectionsToLoad([]);
      setAutoSelectCurrentIndex(0);
      return;
    }

    // If the active section doesn't match our target, switch to it
    if (activeSectionInfo?.sectionId !== currentTarget.sectionId) {
      setActiveSectionInfo(currentTarget);
      return;
    }

    // If the active section matches AND we've finished loading:
    // We can assume the item wasn't found (because the other useEffect would have cleared the state)
    // So we move to the next section
    if (!itemsLoading && currentSectionItems) {
      setAutoSelectCurrentIndex(prev => prev + 1);
    }
  }, [
    autoSelectCurrentIndex,
    autoSelectSectionsToLoad,
    currentCourse?.watchItemId,
    activeSectionInfo,
    itemsLoading,
    currentSectionItems
  ]);









  const getItemLabel = ({ itemId, itemType, sectionItems, sectionId }: LabelOptions): string => {
    const item = (sectionItems[sectionId] || []).find(i => i._id === itemId);
    return item?.name || item?.title || 'Untitled';
  };

  // Add Module
  // const handleAddModule = () => {
  //   if (!versionId) return;
  //   createModuleAsync({
  //     params: { path: { versionId } },
  //     body: { name: "Untitled Module", description: "Module description" }
  //   }).then((res) => {
  //     refetchVersion();
  //     if (shouldFetchItems) {
  //       refetchItems();
  //     }
  //     setIsEditingModule(true);
  //     setOriginalModuleData({ name: "Untitled Module", description: "Module description" });
  //   });
  // };

  const handleAddModule = async () => {
    if (!versionId) return;

    try {
      await createModuleAsync({
        params: { path: { versionId } },
        body: {
          name: "Untitled Module",
          description: "Module description",
        },
      });

    } catch (error: any) {
      // Enhanced error message extraction for backend validation errors
      let message = "Failed to create module";

      if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.response?.data?.error) {
        message = error.response.data.error;
      } else if (error?.message) {
        message = error.message;
      } else if (typeof error === 'string') {
        message = error;
      }

      toast.error(message);
    }

    setIsEditingModule(true);
    setOriginalModuleData({
      name: "Untitled Module",
      description: "Module description",
    });


  };


  // Invalidate all related queries
  const invalidateAllQueries = async () => {
    // Invalidate section items queries as in refetchitems
    await queryClient.invalidateQueries({
      queryKey: ["get", "/courses/versions/{versionId}/modules/{moduleId}/sections/{sectionId}/items"]
    })

    // Invalidate all item detail queries as in refetchitem
    await queryClient.invalidateQueries({
      queryKey: ["get", "/courses/{courseId}/versions/{versionId}/item/{itemId}"]
    })

    // // Invalidate all course version queries
    await queryClient.invalidateQueries({
      queryKey: ["get", "/courses/versions/{id}"]
    })
  }

  // invalidated as sometimes questions are not fetched properly
  const handleinvalidateItemQueries = async () => {
    // Invalidate all related queries for question banks
    await queryClient.invalidateQueries({
      queryKey: ["get", "/quizzes/question-bank/{questionBankId}"]
    })

    // Invalidate all related queries for questions
    await queryClient.invalidateQueries({
      queryKey: ["get", "/quizzes/questions/{questionId}"]
    })
  }


  // Process CSV file and create items
  const processCSV = async (file: File, moduleId: string, sectionId: string, youtubeUrl: string) => {
    setIsProcessingCSV(true);
    try {
      setShowCSVUpload(false);
      const text = await file.text();
      const result = Papa.parse<CSVRow>(text, {
        header: true,
        skipEmptyLines: true,
        // Collapse internal whitespace and trim so headers match the backend's
        // normalizeKey (e.g. "Correct  Answer" -> "Correct Answer").
        transformHeader: (h) => h.replace(/\s+/g, ' ').trim()
      });

      // Validate CSV structure
      if (!result.data.length) {
        throw new Error('CSV file is empty');
      }

      // Validate YouTube URL format
      const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
      if (!youtubeRegex.test(youtubeUrl)) {
        throw new Error('Please provide a valid YouTube URL (e.g., https://www.youtube.com/watch?v=... or https://youtu.be/...)');
      }

      // Validate required columns
      const requiredColumns = ['Segment', 'Question', 'Correct Answer'];
      const firstRow = result.data[0];
      const missingColumns = requiredColumns.filter(col => !(col in firstRow));

      if (missingColumns.length > 0) {
        throw new Error(
          `Missing required columns: ${missingColumns.join(', ')}. ` +
          `Found columns: ${Object.keys(firstRow).join(', ')}`
        );
      }


      await userCSVtoItem.mutateAsync({
        params: { path: { courseId: courseId!, versionId: versionId!, moduleId, sectionId } },
        body: { youtubeurl: youtubeUrl, data: result.data }
      });

      // Success toast is shown by the upload dialog; just refresh the data here.
      await invalidateAllQueries();
      setIsProcessingCSV(false);
    } catch (error: any) {
      console.error('Error processing CSV:', error);
      // Surface the real failure to the caller instead of swallowing it, so the
      // upload dialog can show the error (and not a false "uploaded" success).
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (error instanceof Error ? error.message : 'Failed to process CSV');
      throw new Error(message);
    } finally {
      setIsProcessingCSV(false);
    }
  };



  // Handle file input change
  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>, moduleId: string, sectionId: string) => {
    const file = e.target.files?.[0];
    if (file) {
      processCSV(file, moduleId, sectionId, youtubeUrl);
    }
    // Reset the input
    e.target.value = '';
  };

  // Add Section
  const handleAddSection = (moduleId: string) => {
    if (!versionId) return;
    createSectionAsync({
      params: { path: { versionId, moduleId } },
      body: { name: "New Section", description: "Section description" }
    }).then((res) => {
      refetchVersion();
      if (shouldFetchItems) {
        refetchItems();
      }
    });
  };

  const handleHideModule = async (moduleId: string, hide: boolean) => {
    if (!versionId) return;
    setHidingModuleId(moduleId);
    try {
      await hideModuleAsync({
        params: { path: { versionId, moduleId } },
        body: { hide: hide }
      });
      refetchVersion();
    } finally {
      setHidingModuleId(null);
    }
  }

  const handleHideSection = async (moduleId: string, sectionId: string, hide: boolean) => {
    if (!versionId) return;
    setHidingSectionId(sectionId);
    try {
      await hideSectionAsync({
        params: { path: { versionId, moduleId, sectionId } },
        body: { hide: hide }
      });
      refetchVersion();
    } finally {
      setHidingSectionId(null);
    }
  }

  const handleHideItem = async (itemId: string, hide: boolean) => {
    if (!versionId) return;
    setHidingItemId(itemId);
    try {
      await updateItemVisibilityAsync({
        params: { path: {courseId, versionId, itemId } },
        body: { hide: hide }
      });

      refetchVersion();
      refetchItems();
    } catch (error) {
      console.error("? Error in handleHideItem:", error);
    } finally {
      setHidingItemId(null);
    }
  }

  // Add Item (handles all item types including video, quiz, article, and project)
  const handleAddItem = (moduleId: string, sectionId: string, type: string, videoData?: any) => {
    if (!versionId) return;

    type ItemType = "VIDEO" | "QUIZ" | "BLOG" | "PROJECT" | "FEEDBACK" | "REFLECTION";
    const typeMap: Record<string, ItemType> = {
      video: "VIDEO",
      quiz: "QUIZ",
      article: "BLOG",
      project: "PROJECT",
      feedback: "FEEDBACK",
      reflection: "REFLECTION"
    };

    // Handle video items
    if (type === "VIDEO" && videoData) {
      createItemAsync({
        params: { path: { versionId, moduleId, sectionId } },
        body: {
          type: "VIDEO",
          name: videoData.name,
          description: videoData.description,
          videoDetails: {
            // URL and source/assetId are mutually exclusive — send only the pair
            // the modal actually produced. Listing URL unconditionally would send
            // it as undefined for an upload, and the backend would then validate
            // the item as a YouTube video and reject it for a missing URL.
            ...(videoData.details.source === "GCS"
              ? {
                source: videoData.details.source,
                assetId: videoData.details.assetId,
              }
              : { URL: videoData.details.URL }),
            startTime: videoData.details.startTime,
            endTime: videoData.details.endTime,
            points: videoData.details.points,
          }
        }
      }).then((res) => {
        refetchVersion();
        if (shouldFetchItems) {
          refetchItems();
        }
        toast.success("Video created successfully");
      }).catch((error) => {
        console.error("Error creating video:", error);
        toast.error(`Failed to create video: ${error.message || 'Unknown error'}`);
      });

      return;
    }
    if (type === "QUIZ") {
      createItemAsync({
        params: {
          path: { versionId, moduleId, sectionId },
        },
        body: {
          type: typeMap[type],
          name: `New ${typeMap[type]}`,
          description: "Sample content",
        },
      }).then((res) => {
        refetchVersion();
        if (shouldFetchItems) {
          refetchItems();
        }
        toast.success("Quiz created successfully");
      }).catch((error) => {
        console.error("Error creating quiz:", error);
        toast.error(`Failed to create quiz: ${error.message || 'Unknown error'}`);
      });
    }
    if (type === "article") {
      createItemAsync({
        params: {
          path: { versionId, moduleId, sectionId },
        },
        body: {
          type: typeMap[type],
          name: `New ${typeMap[type]}`,
          description: "Sample content",
          blogDetails: {
            content: "Sample content",
            points: "2.0",
            estimatedReadTimeInMinutes: 1,
          },
        },
      }).then((res) => {
        refetchVersion();
        if (shouldFetchItems) {
          refetchItems();
        }
        toast.success("Article created successfully");
      }).catch((error) => {
        console.error("Error creating article:", error);
        toast.error(`Failed to create article: ${error.message || 'Unknown error'}`);
      });
    }
    if (type === "project") {
      createItem.mutate({
        params: { path: { versionId, moduleId, sectionId } },
        body: {
          type: typeMap[type], name: `New ${typeMap[type]}`,
          description: "Project description"
        }
      })
        .then(() => {
          refetchVersion();
          if (shouldFetchItems) {
            refetchItems();
          }
          toast.success("Project created successfully");
        })
        .catch((error) => {
          console.error("Error creating project:", error);
          toast.error(`Failed to create project: ${error.message || 'Unknown error'}`);
        });
    }
    if (type === "feedback") {
      createItemAsync({
        params: {
          path: {
            versionId: versionId!,
            moduleId: module.moduleId,
            sectionId: section.sectionId,
          },
        },
        body: {
          type: typeMap[type],
          name: "Feedback Form",
          description: "Submit your feedback about the previous video/quiz",
          feedbackFormDetails: {
            jsonSchema: {
              type: 'object',
              properties: {
                Name: {
                  type: 'string',
                  title: 'Name',
                  minLength: 1,
                },
                Email: {
                  type: 'string',
                  format: 'email',
                  title: 'Email',
                },
                Feedback: {
                  type: 'string',
                  title: 'Feedback',
                  minLength: 10
                },
              },
              required: ['Name', 'Email', 'Feedback'],
            },
            uiSchema: {
              Name: {
                'ui:placeholder': 'Enter your Name',
              },
              Email: {
                'ui:placeholder': 'Enter your Email',
              },
              Feedback: {
                'ui:placeholder': 'Enter your feedback here...',
              },
            }
          },
        }
      })
        .then((created) => {
          const newItem = created?.createdItem || created?.item || created?.data || created;
          const itemsGroupId = created?.itemsGroup?._id || section.itemsGroupId;

          if (newItem && newItem._id) {
            // Auto-select the newly created feedback form
            setSelectedItem({ id: newItem._id, name: "Feedback Form 1" });
            setSelectedEntity({
              type: "item",
              data: newItem,
              parentIds: {
                moduleId: module.moduleId,
                sectionId: section.sectionId,
                itemsGroupId,
              },
            });
          } else {
            refetchVersion();
            if (shouldFetchItems) {
              refetchItems();
            }
          }
        })
        .catch((err) => {
          toast.error("Failed to create feedback form: ", err.message);
          console.error(err);
        });
    }
    if (type === "csv_upload") {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.csv';
      input.onchange = (e) => handleFileUpload(e as unknown as ChangeEvent<HTMLInputElement>, moduleId, sectionId);
      input.click();
    }
  };

  type QuizCsvRow = {
    Question?: string;
    Hint?: string;
    'Option A'?: string;
    'Option B'?: string;
    'Option C'?: string;
    'Option D'?: string;
    'Expln-A'?: string;
    'Expln-B'?: string;
    'Expln-C'?: string;
    'Expln-D'?: string;
    'Correct Answer'?: string;
  };

  // Creates the quiz item + populates its question bank from an uploaded CSV.
  // Defers item creation until the file is actually being uploaded so cancelling
  // the dialog doesn't leave an orphan empty quiz behind.
  const handleQuizCsvUpload = async (file: File) => {
    if (!versionId || !pendingQuizContext) {
      throw new Error("Missing course/section context for quiz creation.");
    }
    const { moduleId, sectionId } = pendingQuizContext;

    const parsed = (Papa as any).parse(await file.text(), {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h: string) => h.trim(),
    }) as { data: QuizCsvRow[]; errors: Array<{ message: string }> };
    if (parsed.errors.length) {
      throw new Error(`CSV parse error: ${parsed.errors[0].message}`);
    }
    const rows: QuizCsvRow[] = parsed.data.filter((r: QuizCsvRow) => (r.Question ?? '').trim().length > 0);
    if (rows.length === 0) {
      throw new Error("CSV has no question rows.");
    }

    const requiredCols = ['Question', 'Option A', 'Option B', 'Option C', 'Option D', 'Correct Answer'] as const;
    rows.forEach((row: QuizCsvRow, i: number) => {
      const missing = requiredCols.filter(c => !((row as any)[c]?.toString().trim()));
      if (missing.length) {
        throw new Error(`Row ${i + 1}: missing ${missing.join(', ')}.`);
      }
      const ans = (row['Correct Answer'] ?? '').toString().trim().toUpperCase();
      if (!['A', 'B', 'C', 'D'].includes(ans)) {
        throw new Error(`Row ${i + 1}: Correct Answer must be A, B, C, or D.`);
      }
    });

    // 1. Create the quiz item now that we know we'll populate it.
    // Backend requires a fully-populated quizDetails when type === 'QUIZ';
    // we pick sensible defaults matched to the imported CSV. Teacher can edit
    // these later in the quiz settings dialog.
    const created = await createItemAsync({
      params: { path: { versionId, moduleId, sectionId } },
      body: {
        type: 'QUIZ',
        name: 'New QUIZ',
        description: 'Imported from CSV',
        quizDetails: {
          passThreshold: 0.7,
          maxAttempts: -1,
          quizType: 'NO_DEADLINE',
          approximateTimeToComplete: '00:30:00',
          allowPartialGrading: true,
          allowHint: true,
          allowSkip: true,
          showCorrectAnswersAfterSubmission: true,
          showExplanationAfterSubmission: true,
          showScoreAfterSubmission: true,
          questionVisibility: rows.length,
          releaseTime: new Date().toISOString(),
          questionBankRefs: [],
        },
      },
    });
    const newQuiz: any =
      (created as any)?.createdItem || (created as any)?.item || (created as any)?.data;
    const newQuizId: string | undefined = newQuiz?._id || newQuiz?.itemId;
    if (!newQuizId) {
      throw new Error("Quiz was created but no ID was returned.");
    }

    // 2. Create one question per row.
    const questionIds: string[] = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const correct = (row['Correct Answer'] ?? '').toString().trim().toUpperCase() as 'A' | 'B' | 'C' | 'D';
      const opts: Record<'A' | 'B' | 'C' | 'D', string> = {
        A: row['Option A']!.toString(),
        B: row['Option B']!.toString(),
        C: row['Option C']!.toString(),
        D: row['Option D']!.toString(),
      };
      // Backend requires non-empty explaination for every lot item; fall back
      // when the CSV cell is blank or missing.
      const explFallback = "No explanation provided.";
      const expl: Record<'A' | 'B' | 'C' | 'D', string> = {
        A: row['Expln-A']?.toString().trim() || explFallback,
        B: row['Expln-B']?.toString().trim() || explFallback,
        C: row['Expln-C']?.toString().trim() || explFallback,
        D: row['Expln-D']?.toString().trim() || explFallback,
      };
      const incorrectKeys = (['A', 'B', 'C', 'D'] as const).filter(k => k !== correct);

      const res = await createQuestion.mutateAsync({
        body: {
          question: {
            text: row.Question!.toString(),
            type: 'SELECT_ONE_IN_LOT',
            isParameterized: false,
            parameters: [],
            hint: row.Hint?.toString() ?? '',
            timeLimitSeconds: 60,
            points: 1,
            priority: 'MEDIUM',
          },
          solution: {
            correctLotItem: { text: opts[correct], explaination: expl[correct] },
            incorrectLotItems: incorrectKeys.map(k => ({
              text: opts[k],
              explaination: expl[k],
            })),
          },
        },
      });
      questionIds.push(res.questionId);
    }

    // 3. Bundle the questions into a bank.
    const bankRes = await createQuestionBank.mutateAsync({
      body: {
        courseId,
        courseVersionId: versionId,
        questions: questionIds,
        title: 'Imported Questions',
        description: 'Imported from CSV',
      },
    });

    // 4. Attach the bank to the new quiz.
    await addQuestionBankToQuiz.mutateAsync({
      params: { path: { quizId: newQuizId } },
      body: { bankId: bankRes.questionBankId, count: questionIds.length },
    });

    refetchVersion();
    if (shouldFetchItems) refetchItems();
    setActiveSectionInfo({ moduleId, sectionId });
    setPendingQuizContext(null);
  };

  const navigate = useNavigate();

  // Interim state of modules
  const pendingOrder = useRef<typeof module[]>(modules);

  const pendingOrderSections = useRef<Record<string, any[]>>({});
  // Interim state of items
  const pendingOrderItems = useRef<typeof sectionItems>(sectionItems);

  // Move module
  const handleMoveModule = async (moduleId: string, versionId?: string) => {
    try {
      const newList = pendingOrder.current;
      const newIndex = newList.findIndex((mod: any) => mod.moduleId === moduleId);

      const before = newList[newIndex + 1] || null;
      const after = newList[newIndex - 1] || null;

      if (versionId && moduleId) {
        await moveModuleAsync({
          params: {
            path: { versionId, moduleId },
          },
          body: before
            ? { beforeModuleId: before?.moduleId || "" }
            : { afterModuleId: after?.moduleId || "" },
        });

        refetchVersion();
      }
    } catch (error) {
      toast.error(error?.message|| "Failed to move module");
    }
  };

  const handleMoveSection = async (moduleId: string, sectionId: string, versionId: string) => {
    const snapshot = initialModules.map(m => ({ ...m, sections: [...(m.sections || [])] }));
    try {
      const order = pendingOrderSections.current[moduleId]; // ✅ was pendingOrder.current[moduleId]
      if (!order) return;

      const movedIndex = order.findIndex((s) => s.sectionId === sectionId);
      if (movedIndex === -1) return;

      const after = order[movedIndex - 1] || null;
      const before = order[movedIndex + 1] || null;

      await moveSectionAsync({ 
          params: {
            path: { versionId, moduleId, sectionId },
          },
          body: before
            ? { beforeSectionId: before.sectionId }
            : after
            ? { afterSectionId: after.sectionId }
            : {}, });
      refetchVersion();
    } catch (error) {
      toast.error(error?.message || "Failed to move section");
      setInitialModules(snapshot);
    }
  };
  
  // Move item
  const handleMoveItem = async (
    moduleId: string,
    sectionId: string,
    itemId: string,
    versionId: string
  ) => {
    try {
      const order = pendingOrderItems.current[sectionId];
      if (!order) return;

      const movedIndex = order.findIndex((i) => i._id === itemId);
      if (movedIndex === -1) return;

      const after = order[movedIndex - 1] || null;
      const before = order[movedIndex + 1] || null;

      await moveItemAsync({
        params: {
          path: { versionId, moduleId, sectionId, itemId },
        },
        body: before
          ? { beforeItemId: before._id }
          : after
          ? { afterItemId: after._id }
          : {},
      });

      if (shouldFetchItems) {
        refetchItems();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to move item");
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedEntity || !versionId) return;

    const { type, data, parentIds } = selectedEntity;

    try {
      if (type === "module") {
        await deleteModuleAsync({
          params: {
            path: {
              versionId,
              moduleId: data.moduleId,
            },
          },
        });

        setExpandedModules(prev => ({
          ...prev,
          [data.moduleId]: false,
        }));
        setIsEditingModule(false);
      }

      if (type === "section" && parentIds?.moduleId) {
        if (activeSectionInfo?.sectionId === data.sectionId) {
          setActiveSectionInfo(null);
        }

        await deleteSectionAsync({
          params: {
            path: {
              versionId,
              moduleId: parentIds.moduleId,
              sectionId: data.sectionId,
            },
          },
        });

        setExpandedSections(prev => ({
          ...prev,
          [data.sectionId]: false,
        }));
        setIsEditingSection(false);
      }

      refetchVersion();
      if (shouldFetchItems) refetchItems();
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedEntity(null);
      setErrors({ title: "", description: "" });
    }
  };



  useEffect(() => {
    if (modules.length > 0)
      setInitialModules(modules)
  }, [modules])

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full w-full">
      {/* Show loading overlay when processing CSV */}
      {isProcessingCSV && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-background p-6 rounded-lg shadow-xl flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
            <p className="text-lg font-medium">Processing CSV file...</p>
            <p className="text-sm text-muted-foreground">This may take a moment</p>
          </div>
        </div>
      )}
      {/* CSV Upload Modal */}
      {/* <Dialog open={showCSVUpload} onOpenChange={setShowCSVUpload}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Questions</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="youtube-url">YouTube Video URL</Label>
              <Input
                id="youtube-url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The video that these questions are based on
              </p>
            </div>

            <div className="space-y-2">
              <Label>Questions CSV File</Label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    const file = e.dataTransfer.files[0];
                    if (file.type === "text/csv" || file.name.endsWith('.csv')) {
                      setSelectedCSVFile(file);
                    } else {
                      toast.error("Please upload a valid CSV file");
                    }
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onClick={() => document.getElementById('csv-upload')?.click()}
              >
                <div className="space-y-2">
                  <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    CSV file with questions (max 10MB)
                  </p>
                  {selectedCSVFile && (
                    <p className="text-sm font-medium text-foreground mt-2">
                      Selected: {selectedCSVFile.name}
                    </p>
                  )}
                </div>
                <input
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setSelectedCSVFile(e.target.files[0]);
                    }
                  }}
                />
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              <p className="font-medium mb-1">CSV Format:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>First row should be the header with column names</li>
                <li>Required columns: Segment, Question, Option A, Option B, Option C, Option D, Correct Answer</li>
                <li>Segment: Numeric value to group questions</li>
                <li>Correct Answer: Should be A, B, C, or D</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCSVUpload(false);
                setYoutubeUrl('');
                setSelectedCSVFile(null);
              }}
              disabled={isProcessingCSV}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!youtubeUrl) {
                  toast.error("Please enter a YouTube URL");
                  return;
                }

                if (!selectedCSVFile) {
                  toast.error("Please select a CSV file");
                  return;
                }

                try {
                  setIsProcessingCSV(true);
                  await processCSV(selectedCSVFile, activeSectionInfo.moduleId, activeSectionInfo.sectionId, youtubeUrl);
                  toast.success("CSV uploaded and processed successfully!");
                  setShowCSVUpload(false);
                  setYoutubeUrl('');
                  setSelectedCSVFile(null);
                  refetchVersion();
                  if (shouldFetchItems) {
                    refetchItems();
                  }
                } catch (error) {
                  console.error("Error processing CSV:", error);
                  toast.error(error instanceof Error ? error.message : "Failed to process CSV");
                } finally {
                  setIsProcessingCSV(false);
                }
              }}
              disabled={!youtubeUrl || !selectedCSVFile || isProcessingCSV}
            >
              {isProcessingCSV ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog> */}
      <QuestionUploadDialog
        open={showCSVUpload}
        onOpenChange={setShowCSVUpload}
        courseId={courseId}
        versionId={versionId}
        onUploadComplete={async (youtubeUrl: string, csvFile: File) => {
          // Let errors propagate so QuestionUploadDialog can report the real
          // failure rather than showing a false "Content uploaded" success.
          await processCSV(
            csvFile,
            activeSectionInfo?.moduleId,
            activeSectionInfo?.sectionId,
            youtubeUrl
          );
        }}
      />

      {/* Quiz creation: blank vs upload-CSV choice */}
      <Dialog
        open={quizChoiceOpen}
        onOpenChange={(open) => {
          setQuizChoiceOpen(open);
          if (!open) setPendingQuizContext(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Add Quiz</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            How would you like to start this quiz?
          </p>
          <div className="grid gap-2 pt-2">
            <Button
              variant="default"
              onClick={() => {
                if (!pendingQuizContext) return;
                const { moduleId, sectionId } = pendingQuizContext;
                setQuizModuleId(moduleId);
                setQuizSectionId(sectionId);
                if (currentCourse) {
                  setCurrentCourse({ ...currentCourse, moduleId, sectionId });
                }
                setQuizWizardOpen(true);
                setQuizChoiceOpen(false);
                setPendingQuizContext(null);
              }}
            >
              Start blank
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setQuizChoiceOpen(false);
                setQuizCsvDialogOpen(true);
              }}
              disabled={!pendingQuizContext}
            >
              Upload CSV to populate questions
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quiz-from-CSV upload (no YouTube URL, no Smart Upload) */}
      <QuestionUploadDialog
        open={quizCsvDialogOpen}
        mode="quiz-questions"
        onOpenChange={(open) => {
          setQuizCsvDialogOpen(open);
          if (!open) setPendingQuizContext(null);
        }}
        onUploadComplete={async (_youtubeUrl: string, csvFile: File) => {
          await handleQuizCsvUpload(csvFile);
        }}
      />

      {/* Mobile Sidebar Overlay */}
      {/* {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )} */}
      {/* {isDesktopSidebarVisible && ( */}
      <SidebarResizablePanel
        defaultSize={20}
        minSize={20}
        maxSize={50}
      // className={`${isMobileSidebarOpen ? 'fixed inset-y-0 left-0 z-50 w-[280px]' : 'hidden md:block'}`}
      >
        {/* sidebar content */}
        <div className="h-full overflow-hidden border-r border-border/40 bg-sidebar/50">
          <Sidebar variant="sidebar" collapsible="none" className="h-screen w-full">
            <SidebarHeader>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3 px-3 py-2">
                  <BookOpen className="text-primary" />
                  <div>
                    <h1 className="text-base font-bold">Vibe (Teacher)</h1>
                    <p className="text-xs text-muted-foreground">Course Editor</p>
                  </div>
                </div>
                <TooltipProvider>
                  <div className="flex items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground"
                        onClick={handleExportQuestionBank}
                        disabled={isExportingQuestionBank || !courseId || !versionId}
                      >
                        {isExportingQuestionBank ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        <span className="sr-only">Export question bank as CSV</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {isExportingQuestionBank ? "Exporting…" : "Export all quiz questions (CSV)"}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant={isReorderEnabled ? "default" : "ghost"}
                        className={`h-7 w-7 transition-all duration-200 ${isReorderEnabled ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                        onClick={() => setIsReorderEnabled((prev) => !prev)}
                      >
                        {isReorderEnabled ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                        <span className="sr-only">Toggle Reorder</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {isReorderEnabled ? "Disable Reordering" : "Enable Reordering"}
                    </TooltipContent>
                  </Tooltip>
                  </div>
                </TooltipProvider>
              </div>
              <Separator className="opacity-50" />
            </SidebarHeader>

            <SidebarContent
              className="bg-card/50 pl-2"

            >
              <ScrollArea className="flex-1">
                <Reorder.Group
                  axis="y"
                  onReorder={(newOrder) => {
                    pendingOrder.current = newOrder;
                  }}
                  values={initialModules}
                >
                  <SidebarMenu className="space-y-2 text-sm pr-1 pt-2">
                    {initialModules
                      .slice()
                      .sort((a: any, b: any) => a.order.localeCompare(b.order))
                      .map((module: any) => (
                        <SidebarMenuItem key={module.moduleId}>
                          <Reorder.Item
                            key={module.moduleId}
                            value={module}
                            as="div"
                            drag={isReorderEnabled}
                            className={module.isHidden ? "focus:outline-none opacity-60" : "focus:outline-none"}
                            whileDrag={{ scale: 1.02 }}
                            onDragEnd={() => {
                              setInitialModules(pendingOrder.current);
                              handleMoveModule(module.moduleId, versionId);
                            }}
                          >
                            <Button className="absolute top-0 right-0" size="icon" variant="ghost" onClick={(e) => handleHideModule(module.moduleId, !module.isHidden)} disabled={hidingModuleId === module.moduleId}>
                              {hidingModuleId === module.moduleId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : !module.isHidden ? (
                                <Eye className="h-4 w-4" />
                              ) : (
                                <EyeOff className="h-4 w-4" />
                              )}
                              <span className="sr-only">Hide Module</span>
                            </Button>
                            <SidebarMenuButton
                              onClick={() => {
                                toggleModule(module.moduleId);
                                setSelectedEntity({ type: "module", data: module });
                                setIsEditingModule(false);
                                setOriginalModuleData({
                                  name: module.name,
                                  description: module.description || ""
                                });
                              }}
                            >
                              <ChevronRight
                                className={`h-3.5 w-3.5 transition-transform ${expandedModules[module.moduleId] ? "rotate-90" : ""
                                  }`}
                              />
                              <span className="ml-2 max-w-[35ch] truncate" title={module.name}>{module.name}</span>
                            </SidebarMenuButton>
                          </Reorder.Item>

                          {expandedModules[module.moduleId] && (
                            <Reorder.Group
                              axis="y"
                              values={module.sections}
                              onReorder={(newSectionOrder) => {
                                pendingOrderSections.current[module.moduleId] = newSectionOrder;
                              }}
                            >
                              <SidebarMenuSub className="ml-2">
                                {module.sections?.map((section: any) => (
                                  <Reorder.Item
                                    key={section.sectionId}
                                    value={section}
                                    drag={isReorderEnabled}
                                    className={section.isHidden || module.isHidden ? "focus:outline-none opacity-60" : "focus:outline-none"}
                                    whileDrag={{ scale: 1.02 }}
                                    onDragEnd={() => {
                                      setInitialModules((prev) =>
                                        prev.map((mod) =>
                                          mod.moduleId === module.moduleId
                                            ? { ...mod, sections: pendingOrderSections.current[module.moduleId] ?? mod.sections }
                                            : mod
                                        )
                                      );
                                      handleMoveSection(module.moduleId, section.sectionId, versionId);
                                    }}
                                  >

                                    <div
                                      data-slot="sidebar-menu-sub-item"
                                      data-sidebar="menu-sub-item"
                                      className="group/menu-sub-item relative"
                                    >
                                      <SidebarMenuSubButton
                                        onClick={() => {
                                          setMode("default");
                                          toggleSection(module.moduleId, section.sectionId);
                                          setSelectedEntity({
                                            type: "section",
                                            data: section,
                                            parentIds: { moduleId: module.moduleId },
                                          });
                                          setIsEditingSection(false);
                                          setOriginalSectionData({
                                            name: section.name,
                                            description: section.description || ""
                                          });
                                        }}
                                      >
                                        <ChevronRight
                                          className={`h-3 w-3 transition-transform ${expandedSections[section.sectionId] ? "rotate-90" : ""
                                            }`}
                                        />
                                        <span className="ml-2 truncate  max-w-[25ch] truncate block" title={section.name}
                                        >{section.name} </span>
                                      </SidebarMenuSubButton>
                                      <Button className="absolute top-0 right-0" size="icon" variant="ghost" onClick={(e) => handleHideSection(module.moduleId, section.sectionId, !section.isHidden)} disabled={module.isHidden || hidingSectionId === section.sectionId}>
                                        {hidingSectionId === section.sectionId ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : !section.isHidden ? (
                                          <Eye className="h-4 w-4" />
                                        ) : (
                                          <EyeOff className="h-4 w-4" />
                                        )}
                                        <span className="sr-only">Hide Section</span>
                                      </Button>

                                      {expandedSections[section.sectionId] && (
                                        <Reorder.Group
                                          axis="y"
                                          values={sectionItems[section.sectionId] || []}
                                          onReorder={(newItemOrder) => {
                                            //
                                            pendingOrderItems.current[section.sectionId] = newItemOrder;
                                            //
                                          }}
                                        >
                                          <SidebarMenuSub className="ml-4 space-y-1 pt-1">
                                            {itemsLoading && activeSectionInfo?.sectionId === section.sectionId ? (
                                              <div className="flex items-center justify-center py-4">
                                                <Loader />
                                              </div>
                                            ) : (sectionItems[section.sectionId] || [])
                                              .slice()
                                              .sort((a: any, b: any) => a.order.localeCompare(b.order))
                                              .map((item: any) => (
                                                <Reorder.Item
                                                  key={item._id}
                                                  value={item}
                                                  drag={isReorderEnabled}
                                                  className={section.isHidden || module.isHidden || item.isHidden ? "focus:outline-none opacity-60" : "focus:outline-none"}
                                                  whileDrag={{ scale: 1.02 }}
                                                  onDragEnd={() => {

                                                    setSectionItems((prev) => {
                                                      const items = pendingOrderItems.current[section.sectionId] || prev[section.sectionId];

                                                      // Sort by LexoRank-compatible `order` string
                                                      const sortedItems = [...items].sort((a, b) => a.order.localeCompare(b.order));

                                                      return {
                                                        ...prev,
                                                        [section.sectionId]: sortedItems
                                                      };
                                                    });

                                                    handleMoveItem(module.moduleId, section.sectionId, item._id, versionId);
                                                  }}
                                                >
                                                  <SidebarMenuSubItem key={item._id}>
                                                    <SidebarMenuSubButton
                                                      className={`justify-start ${selectedItem.name === getItemLabel({
                                                        itemId: item._id,
                                                        itemType: item.type,
                                                        sectionItems,
                                                        sectionId: section.sectionId
                                                      }) && selectedItem.id == item._id
                                                        ? "bg-zinc-600 text-gray-200"
                                                        : "bg-transparent transition-none"
                                                        }`}
                                                      onClick={async () => {
                                                        await handleinvalidateItemQueries();
                                                        setMode("default");
                                                        const label = getItemLabel({
                                                          itemId: item._id,
                                                          itemType: item.type,
                                                          sectionItems,
                                                          sectionId: section.sectionId
                                                        });

                                                        setSelectedItem({ id: item._id, name: label });

                                                        // Patch: For PROJECT, ensure name/description are always present at root
                                                        let patchedItem = item;
                                                        if (item.type === 'PROJECT') {
                                                          const details = item.details || {};
                                                          const name = (details.name && details.name.trim()) ? details.name : (item.name || '');
                                                          const description = (details.description && details.description.trim()) ? details.description : (item.description || '');
                                                          patchedItem = {
                                                            ...item,
                                                            name,
                                                            description
                                                          };
                                                        }
                                                        setSelectedEntity({
                                                          type: "item",
                                                          data: patchedItem,
                                                          parentIds: {
                                                            moduleId: module.moduleId,
                                                            sectionId: section.sectionId,
                                                            itemsGroupId: section.itemsGroupId,
                                                          },
                                                        });

                                                        if (checkScreenSize() && (item.type === 'VIDEO' || item.type === 'QUIZ' || item.type === 'BLOG')) {
                                                          setOpenMobile(false);
                                                          setOpen(false);
                                                        }
                                                      }
                                                      }
                                                    >
                                                      {getItemIcon(item.type)}
                                                      <span className={`ml-1 text-xs ${selectedItem.name === getItemLabel({
                                                        itemId: item._id,
                                                        itemType: item.type,
                                                        sectionItems,
                                                        sectionId: section.sectionId
                                                      }) && selectedItem.id == item._id
                                                        ? "text-gray-200"
                                                        : "text-muted-foreground"
                                                        }`}>
                                                        {getItemLabel({
                                                          itemId: item._id,
                                                          itemType: item.type,
                                                          sectionItems,
                                                          sectionId: section.sectionId
                                                        })}
                                                      </span>
                                                    </SidebarMenuSubButton>
                                                    <Button className="absolute  top-0 right-0" size="icon" variant="ghost" onClick={(e) => handleHideItem(item._id, !item.isHidden)} disabled={section.isHidden || module.isHidden || hidingItemId === item._id}>
                                                      {hidingItemId === item._id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                      ) : !item.isHidden ? (
                                                        <Eye className={`h-4 w-4 ${selectedItem.id == item._id
                                                          ? "text-gray-200"
                                                          : "text-muted-foreground"}`} />
                                                      ) : (
                                                        <EyeOff className={`h-4 w-4 ${selectedItem.id == item._id
                                                          ? "text-gray-200"
                                                          : "text-muted-foreground"}`} />
                                                      )}
                                                      <span className="sr-only">Hide Item</span>
                                                    </Button>
                                                    {item.type === 'VIDEO' && (
                                                      <Button
                                                        className="absolute top-0 right-8"
                                                        size="icon"
                                                        variant="ghost"
                                                        title="View student questions for this video"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setStudentQuestionSegment({
                                                            segmentId: item._id,
                                                            segmentTitle: item.name,
                                                          });
                                                        }}
                                                      >
                                                        <MessageSquareQuote
                                                          className={`h-4 w-4 ${selectedItem.id == item._id
                                                            ? "text-gray-200"
                                                            : "text-muted-foreground"}`}
                                                        />
                                                        <span className="sr-only">View student questions</span>
                                                      </Button>
                                                    )}
                                                  </SidebarMenuSubItem>
                                                </Reorder.Item>
                                              ))}
                                            <div className="ml-6 mt-2">

                                              <select

                                                className="text-xs border rounded px-2 py-1 bg-background text-foreground"

                                                defaultValue=""

                                                disabled={module.isHidden || section.isHidden}

                                                onChange={(e) => {

                                                  const type = e.target.value;

                                                  if (type) {

                                                    if (type === "VIDEO") {

                                                      setShowAddVideoModal({

                                                        moduleId: module.moduleId,

                                                        sectionId: section.sectionId,

                                                      });

                                                    } else if (type === "quiz") {

                                                      // Defer creation until the teacher decides between starting
                                                      // blank (existing wizard) and importing questions from a CSV.
                                                      setPendingQuizContext({ moduleId: module.moduleId, sectionId: section.sectionId });
                                                      setQuizChoiceOpen(true);

                                                    }
                                                    else if (type === "project") {

                                                      createItemAsync({
                                                        params: {
                                                          path: {
                                                            versionId: versionId!,
                                                            moduleId: module.moduleId,
                                                            sectionId: section.sectionId,
                                                          },
                                                        },
                                                        body: {
                                                          type: "PROJECT",
                                                          name: `Project name`,
                                                          description: `Project description`
                                                        },
                                                      })
                                                        .then((created) => {
                                                          const newItem = created?.createdItem || created?.item || created?.data || created;
                                                          const itemsGroupId = created?.itemsGroup?._id || section.itemsGroupId;

                                                          if (newItem && newItem._id) {
                                                            setSelectedItem({ id: newItem._id, name: newItem.name });
                                                            setSelectedEntity({
                                                              type: "item",
                                                              data: newItem,
                                                              parentIds: {
                                                                moduleId: module.moduleId,
                                                                sectionId: section.sectionId,
                                                                itemsGroupId,
                                                              },
                                                            });
                                                          } else {
                                                            refetchVersion();
                                                            if (shouldFetchItems) {
                                                              refetchItems();
                                                            }
                                                          }
                                                        });
                                                    }
                                                    else if (type === "feedback") {
                                                      createItemAsync({
                                                        params: {
                                                          path: {
                                                            versionId: versionId!,
                                                            moduleId: module.moduleId,
                                                            sectionId: section.sectionId,
                                                          },
                                                        },
                                                        body: {
                                                          type: "FEEDBACK",
                                                          name: "Feedback Form",
                                                          description: "Submit your feedback about the previous video/quiz",
                                                          feedbackFormDetails: {
                                                            jsonSchema: {
                                                              type: 'object',
                                                              properties: {
                                                                Name: {
                                                                  type: 'string',
                                                                  title: 'Name',
                                                                  minLength: 1,
                                                                },
                                                                Email: {
                                                                  type: 'string',
                                                                  format: 'email',
                                                                  title: 'Email',
                                                                },
                                                                Feedback: {
                                                                  type: 'string',
                                                                  title: 'Feedback',
                                                                  minLength: 10
                                                                },
                                                              },
                                                              required: ['Name', 'Email', 'Feedback'],
                                                            },
                                                            uiSchema: {
                                                              Name: {
                                                                'ui:placeholder': 'Enter your Name',
                                                              },
                                                              Email: {
                                                                'ui:placeholder': 'Enter your Email',
                                                              },
                                                              Feedback: {
                                                                'ui:placeholder': 'Enter your feedback here...',
                                                                'ui:widget': 'textarea',
                                                              },
                                                            }
                                                          },
                                                        }
                                                      })
                                                        .then((created) => {
                                                          const newItem = created?.createdItem || created?.item || created?.data || created;
                                                          const itemsGroupId = created?.itemsGroup?._id || section.itemsGroupId;

                                                          if (newItem && newItem._id) {
                                                            // Auto-select the newly created feedback form
                                                            setSelectedItem({ id: newItem._id, name: "Feedback Form 1" });
                                                            setSelectedEntity({
                                                              type: "item",
                                                              data: newItem,
                                                              parentIds: {
                                                                moduleId: module.moduleId,
                                                                sectionId: section.sectionId,
                                                                itemsGroupId,
                                                              },
                                                            });
                                                          } else {
                                                            refetchVersion();
                                                            if (shouldFetchItems) {
                                                              refetchItems();
                                                            }
                                                          }
                                                        })
                                                        .catch((err) => {
                                                          toast.error("Failed to create feedback form");
                                                          console.error(err);
                                                        });
                                                    }
                                                    else if (type === "reflection") {
                                                      createItemAsync({
                                                        params: {
                                                          path: {
                                                            versionId: versionId!,
                                                            moduleId: module.moduleId,
                                                            sectionId: section.sectionId,
                                                          },
                                                        },
                                                        body: {
                                                          type: "REFLECTION",
                                                          name: "Reflection",
                                                          description: "Write what you learned, then review your peers anonymously",
                                                          reflectionDetails: {},
                                                        } as any,
                                                      })
                                                        .then(() => {
                                                          refetchVersion();
                                                          if (shouldFetchItems) {
                                                            refetchItems();
                                                          }
                                                          toast.success("Reflection added");
                                                        })
                                                        .catch((err) => {
                                                          toast.error("Failed to add reflection");
                                                          console.error(err);
                                                        });
                                                    }
                                                    else if (type === "case_study") {
                                                      createItemAsync({
                                                        params: {
                                                          path: {
                                                            versionId: versionId!,
                                                            moduleId: module.moduleId,
                                                            sectionId: section.sectionId,
                                                          },
                                                        },
                                                        body: {
                                                          type: "CASE_STUDY",
                                                          name: "Case study",
                                                          description: "Write a structured response to the case, then judge pairs of peers' responses",
                                                          caseStudyDetails: {},
                                                        } as any,
                                                      })
                                                        .then(() => {
                                                          refetchVersion();
                                                          if (shouldFetchItems) {
                                                            refetchItems();
                                                          }
                                                          toast.success("Case study added");
                                                        })
                                                        .catch((err) => {
                                                          toast.error("Failed to add case study");
                                                          console.error(err);
                                                        });
                                                    }
                                                    else if (type === "csv_upload") {
                                                      setActiveSectionInfo({ moduleId: module.moduleId, sectionId: section.sectionId });
                                                      setShowCSVUpload(true);
                                                    }
                                                    else {
                                                      setActiveSectionInfo({ moduleId: module.moduleId, sectionId: section.sectionId });
                                                      handleAddItem(module.moduleId, section.sectionId, type);

                                                    }

                                                    e.target.value = "";

                                                  }

                                                }}

                                              >

                                                <option value="" disabled>Add Item</option>

                                                <option value="article">Article</option>

                                                <option value="VIDEO">Video</option>

                                                <option value="quiz">Quiz</option>

                                                <option value="feedback">Feedback Form</option>

                                                <option value="reflection">Reflection (peer reviewed)</option>

                                                <option value="case_study">Case Study (peer reviewed)</option>

                                                <option
                                                  value="project"
                                                  disabled={hasExistingProject}
                                                  className={hasExistingProject ? 'text-gray-400' : ''}
                                                >
                                                  {hasExistingProject ? 'Project (Limit 1 per course)' : 'Project'}
                                                </option>
                                                <option value="csv_upload">Upload CSV</option>

                                              </select>
                                              <TooltipProvider>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <DropdownMenu>
                                                      <DropdownMenuTrigger asChild>
                                                        <Button
                                                          type="button"
                                                          className="inline-flex items-center justify-center px-1.5 py-0 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold text-[10px] gap-0.5 shadow transition-all duration-200 hover:scale-105 hover:shadow-lg hover:from-purple-600 hover:to-indigo-600 focus:outline-none focus:ring-2 focus:ring-purple-400 ml-3"
                                                          style={{ minWidth: 'unset', height: '1.5rem' }}
                                                        >
                                                          <Sparkles className="h-2 w-2" />
                                                          <span>AI</span>
                                                        </Button>
                                                      </DropdownMenuTrigger>
                                                      <DropdownMenuContent align="start" className="w-40">
                                                        <DropdownMenuItem
                                                          className="text-xs cursor-pointer"
                                                          onClick={() => {
                                                            setCurrentCourse({
                                                              courseId,
                                                              versionId,
                                                              moduleId: module.moduleId,
                                                              sectionId: section.sectionId,
                                                              itemId: null,
                                                              watchItemId: null,
                                                            });
                                                            setMode('custom')
                                                            // navigate({ to: '/teacher/ai-section' });
                                                          }}
                                                        >
                                                          Custom mode
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                          className="text-xs cursor-pointer"
                                                          onClick={() => {
                                                            setCurrentCourse({
                                                              courseId,
                                                              versionId,
                                                              moduleId: module.moduleId,
                                                              sectionId: section.sectionId,
                                                              itemId: null,
                                                              watchItemId: null,
                                                            });
                                                            setMode('wizard')
                                                            // navigate({ to: '/teacher/ai-workflow' });
                                                          }}
                                                        >
                                                          Wizard mode
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                          className="text-xs cursor-pointer"
                                                          onClick={() => {
                                                            setCurrentCourse({
                                                              courseId,
                                                              versionId,
                                                              moduleId: module.moduleId,
                                                              sectionId: section.sectionId,
                                                              itemId: null,
                                                              watchItemId: null,
                                                            });
                                                            setActiveSectionInfo({ moduleId: module.moduleId, sectionId: section.sectionId });
                                                            setMode('smartBloom')
                                                          }}
                                                        >
                                                          Smart Bloom&apos;s mode
                                                        </DropdownMenuItem>
                                                         <DropdownMenuItem
                                                            className="text-xs cursor-pointer"
                                                            onClick={() => {
                                                              setCurrentCourse({
                                                                courseId,
                                                                versionId,
                                                                moduleId: module.moduleId,
                                                                sectionId: section.sectionId,
                                                                itemId: null,
                                                                watchItemId: null,
                                                              });
                                                              setMode('ai-module')
                                                              // navigate({ to: '/teacher/ai-module' });
                                                            }}
                                                          >
                                                            AI Module Mode
                                                          </DropdownMenuItem>
                                                          <DropdownMenuItem
                                                            className="text-xs cursor-pointer"
                                                            onClick={() => {
                                                              setCurrentCourse({
                                                                courseId,
                                                                versionId,
                                                                moduleId: module.moduleId,
                                                                sectionId: section.sectionId,
                                                                itemId: null,
                                                                watchItemId: null,
                                                              });
                                                              setMode('advanced')
                                                            }}
                                                          >
                                                            Advanced mode
                                                          </DropdownMenuItem>
                                                      </DropdownMenuContent>
                                                    </DropdownMenu>
                                                  </TooltipTrigger>
                                                  <TooltipContent side="right" align="center">
                                                    Generate Section with AI
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                            </div>

                                          </SidebarMenuSub>
                                        </Reorder.Group>
                                      )}
                                    </div>
                                  </Reorder.Item>
                                ))}

                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="ml-4 mt-2 w-[220px] h-6 text-xs"
                                  onClick={() => handleAddSection(module.moduleId)}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add Section
                                </Button>
                              </SidebarMenuSub>
                            </Reorder.Group>
                          )}
                        </SidebarMenuItem>
                      ))}

                    <div className="px-2 pt-3">
                      <Button size="sm" className="w-[250px]  text-xs" onClick={handleAddModule}>
                        <Plus className="h-3 w-3 mr-1" />
                        Add Module
                      </Button>
                    </div>
                  </SidebarMenu>
                </Reorder.Group>


              </ScrollArea>
            </SidebarContent>
            <SidebarFooter className="border-t border-border/40 bg-gradient-to-t from-sidebar/80 to-sidebar/60">
              <SidebarMenu className="space-y-1 pl-2 py-3">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    className="h-9 px-3 w-full rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-accent/20 hover:to-accent/5 hover:shadow-sm"
                  >
                    <Link to="/teacher" className="flex items-center gap-3">
                      <div className="p-1 rounded-md bg-accent/15">
                        <Home className="h-4 w-4 text-accent-foreground" />
                      </div>
                      <span className="text-sm font-medium">Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    className="h-9 px-3 w-full rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-accent/20 hover:to-accent/5 hover:shadow-sm"
                  >
                    <Link to="/teacher" className="flex items-center gap-3">
                      <div className="p-1 rounded-md bg-accent/15">
                        <GraduationCap className="h-4 w-4 text-accent-foreground" />
                      </div>
                      <span className="text-sm font-medium">Courses</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <Separator className="my-2 opacity-50" />

                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    className="h-10 px-3 w-full rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-accent/20 hover:to-accent/5 hover:shadow-sm"
                  >
                    <Link to="/teacher/profile" className="flex items-center gap-3">
                      <Avatar className="h-6 w-6 border border-border/20">
                        <AvatarImage src={user?.avatar} alt={user?.name} />
                        <AvatarFallback className="bg-gradient-to-br from-primary/15 to-primary/5 text-primary font-bold text-xs">
                          {user?.name?.charAt(0).toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 text-left min-w-0">
                        <div className="text-sm font-medium truncate" title={user?.name || 'Profile'}>{user?.name || 'Profile'}</div>
                        <div className="text-xs text-muted-foreground">View Profile</div>
                      </div>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarFooter>
          </Sidebar>
        </div>
      </SidebarResizablePanel>
      {/* )} */}

      {/* <ResizablePanel
        defaultSize={20}
        minSize={20}
        maxSize={50}
        className={`${isMobileSidebarOpen ? 'fixed inset-y-0 left-0 z-50 w-[280px]' : 'hidden md:block'}`}
      >
      
      </ResizablePanel> */}




      {/* Side bar till herer  */}





      <ResizableHandle className="hidden md:flex" />
      {/* {isDesktopSidebarVisible && <ResizableHandle className="hidden md:flex" />} */}


      <ResizablePanel defaultSize={80} className="min-w-0">
        {/* Course Editor Area */}
        <SidebarInset className="max-w-full overflow-hidden flex flex-col">
          <header className="flex h-16 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear sticky top-0 z-50 bg-background">
            <div className="flex w-full items-center justify-between px-4">
              <div className="flex items-center gap-2">
                {/* Add Toggle Button */}
                {/* <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsDesktopSidebarVisible((p) => !p)}
                  className="hidden md:inline-flex"
                > */}
                <SidebarTrigger />
                {/* </Button> */}

                <Separator orientation="vertical" className="mx-2 h-4" />
                <Breadcrumb className="hidden md:flex">
                  <BreadcrumbList>

                    {breadcrumbs.map((item, index) => (
                      <React.Fragment key={index}>

                        {index > 0 && breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                        <BreadcrumbItem>
                          {item.isCurrentPage ? (
                            <BreadcrumbPage className="lg:flex md:hidden">{item.label}</BreadcrumbPage>
                          ) : (
                            <BreadcrumbLink href={item.path} asChild>
                              <Link to={item.path}>{item.label}</Link>
                            </BreadcrumbLink>
                          )}
                        </BreadcrumbItem>
                      </React.Fragment>
                    ))}
                  </BreadcrumbList>
                </Breadcrumb>
                <Link to="/teacher" className="block md:hidden font-medium text-muted-foreground hover:text-foreground">Dashboard</Link>
              </div>

              <div className="flex items-center gap-3">

                <div className="relative" ref={invitesRef}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInvites((prev) => !prev)}
                    className="relative h-10 px-4 text-sm font-medium transition-all duration-300 hover:bg-gradient-to-r hover:from-accent/30 hover:to-accent/10 hover:text-accent-foreground hover:shadow-lg hover:shadow-accent/10 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/10 data-[state=active]:to-primary/5 data-[state=active]:text-primary before:absolute before:inset-0 before:rounded-md before:bg-gradient-to-r before:from-primary/5 before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300"
                  >
                    <UserRoundCheck className="h-4 w-4" />
                    <span className="hidden sm:block ml-2">Invites</span>
                  </Button>

                  {showInvites && <InviteDropdown setPendingInvites={setPendingInvites} pendingInvites={pendingInvites} />}
                </div>

                <ConfirmationModal isOpen={confirmLogout}
                  onClose={() => setConfirmLogout(false)}
                  onConfirm={handleLogout}
                  title={`Confirm Logout`}
                  description="Are you sure you want to log out? You will need to sign in again to access your dashboard."
                />

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmLogout(true)}
                  className="relative  h-10 px-4 text-sm font-medium transition-all duration-300  hover:text-red-600 hover:bg-gradient-to-r hover:from-red-500/10 hover:to-red-400/5 hover:shadow-red-500/10 dark:hover:text-red-400  dark:hover:bg-gradient-to-r dark:over:from-red-500/10 dark:hover:to-red-400/5"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:block ml-2">Logout</span>
                </Button>

                <ThemeToggle />

                <Link to="/teacher/profile" className="group relative">
                  <div className="absolute -inset-2 rounded-full bg-gradient-to-r from-primary/10 via-transparent to-secondary/10 opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:scale-110 blur-sm" />
                  <Avatar className="relative h-9 w-9 cursor-pointer border-2 border-transparent transition-all duration-300 group-hover:border-primary/20 group-hover:shadow-xl group-hover:shadow-primary/20 group-hover:scale-105">
                    <AvatarImage
                      src={user?.avatar || "/placeholder.svg"}
                      alt={user?.name}
                      className="transition-all duration-300"
                    />
                    <AvatarFallback className="bg-gradient-to-br from-primary/15 via-primary/10 to-primary/5 text-primary font-bold text-sm transition-all duration-300 group-hover:from-primary/25 group-hover:to-primary/10">
                      {user?.name?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Link>
              </div>
            </div>
          </header>
          <div className="w-full p-4 sm:p-6">

          <CourseBackButton />  

            {/* {mode === "default" && <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                
                <span className="sr-only">Toggle Menu</span>

                <div className="flex items-center gap-2 bg-muted/40 px-3 py-1.5 rounded-lg border min-w-0 flex-1 sm:flex-none sm:min-w-[200px]">
                  <GraduationCap className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground whitespace-nowrap">Course</p>
                    <h2 className="text-sm font-medium leading-tight truncate">
                      {isLoading ? (
                        <span className="inline-block h-4 w-32 bg-muted rounded animate-pulse"></span>
                      ) : (
                        courseData?.name || 'Untitled Course'
                      )}
                    </h2>
                  </div>
                </div>
              </div>

              {versionData && (
                <div className="flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-primary/10 border-primary/20 text-primary px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap"
                  >
                    Version: {(versionData as any)?.version || (versionData as any)?.name || 'Unknown'} a
                  </Badge>
                </div>
              )}
            </div>} */}

            {mode==="ai-module" ? <AiModule /> : mode === "advanced" ? <AdvancedAiWorkflow /> : mode === "wizard" ? (
              <AiWorkflow />
            ) : mode === "smartBloom" ? (
              <SmartBloomWorkflow
                onUploadComplete={async (uploadedModuleId, uploadedSectionId) => {
                  await invalidateAllQueries();
                  setMode('default');
                  setExpandedModules(prev => ({ ...prev, [uploadedModuleId]: true }));
                  setExpandedSections(prev => ({ ...prev, [uploadedSectionId]: true }));
                  setActiveSectionInfo({ moduleId: uploadedModuleId, sectionId: uploadedSectionId });
                }}
              />
            ) : mode === "custom" ? (
              <AISectionPage />
            ) : (
              selectedEntity ? (
                <div className="bg-white dark:bg-background rounded-2xl shadow-lg border border-slate-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 md:p-6 lg:p-8">
                    {/* Header with breadcrumb */}
                    <div className="mb-6 pb-4 border-b border-slate-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {/* Pencil icon to update module*/}
                          {(selectedEntity.type === "module" || selectedEntity.type === "section") && !isEditingModule && !isEditingSection && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                if (selectedEntity.type === "module") {
                                  setIsEditingModule(true);
                                  setOriginalModuleData({
                                    name: selectedEntity.data.name,
                                    description: selectedEntity.data.description || ""
                                  });
                                } else {
                                  setIsEditingSection(true);
                                  setOriginalSectionData({
                                    name: selectedEntity.data.name,
                                    description: selectedEntity.data.description || ""
                                  });
                                }
                              }}
                              className="h-8 w-8 -ml-2"
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="sr-only">Edit {selectedEntity.type}</span>
                            </Button>
                          )}
                          <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-slate-900 dark:text-gray-100">
                            {selectedEntity.data?.name}
                          </h2>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedEntity.type === "item" && (
                            // <div className="items-center gap-2 bg-muted/40 px-2 py-1 rounded-md border text-sm">
                            //   <div className="flex items-center justify-center gap-1.5">
                            //     <Switch
                            //       id={`optional-${selectedItemData?.item?._id}`}
                            //       checked={selectedItemData?.item?.isOptional || false}
                            //       disabled={updateItemOptional.isPending && togglingItemId === selectedItemData?.item?._id}
                            //       onCheckedChange={async (checked) => {
                            //         if (versionId && selectedItemData?.item?._id) {
                            //           setTogglingItemId(selectedItemData.item._id);
                            //           try {
                            //             await updateItemOptional.mutateAsync({
                            //               params: {
                            //                 path: {
                            //                   versionId: versionId,
                            //                   itemId: selectedEntity?.data?._id
                            //                 }
                            //               },
                            //               body: { isOptional: checked }
                            //             });
                            //             refetchItem();
                            //           } catch (error) {
                            //             toast.error('Failed to update item optional status');
                            //           } finally {
                            //             setTogglingItemId(null);
                            //           }
                            //         }
                            //       }}
                            //       className={cn(
                            //         "data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
                            //         "h-4 w-8",
                            //         "relative",
                            //         "cursor-pointer",
                            //         updateItemOptional.isPending && togglingItemId === selectedItemData?.item?._id
                            //           ? "opacity-70"
                            //           : "opacity-100"
                            //       )}
                            //     >
                            //       {(updateItemOptional.isPending || togglingItemId === selectedItemData?.item?._id) && (
                            //         <Loader2 className="h-2 w-2 animate-spin absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-primary-foreground" />
                            //       )}
                            //     </Switch>
                            //     <Label
                            //       htmlFor={`optional-${selectedEntity?.data?._id}`}
                            //       className="text-lg text-white cursor-pointer"
                            //       title="Students can skip this item if enabled"
                            //     >
                            //       Optional
                            //     </Label>
                            //   </div>
                            //   <div>
                            //     <p className="text-[10px] text-muted-foreground/80">Students can skip this item if enabled</p>
                            //   </div>
                            // </div>
                            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-card">
                              <Switch
                                id={`optional-${selectedItemData?.item?._id}`}
                                checked={selectedItemData?.item?.isOptional || false}
                                disabled={updateItemOptional.isPending && togglingItemId === selectedItemData?.item?._id}
                                onCheckedChange={async (checked) => {
                                  if (versionId && selectedItemData?.item?._id) {
                                    setTogglingItemId(selectedItemData.item._id);
                                    try {
                                      await updateItemOptional.mutateAsync({
                                        params: {
                                          path: {
                                            versionId: versionId,
                                            itemId: selectedEntity?.data?._id
                                          }
                                        },
                                        body: { isOptional: checked }
                                      });
                                      refetchItem();
                                    } catch (error) {
                                      toast.error('Failed to update item optional status');
                                    } finally {
                                      setTogglingItemId(null);
                                    }
                                  }
                                }}
                                className={cn(
                                  "data-[state=checked]:bg-primary",
                                  updateItemOptional.isPending && togglingItemId === selectedItemData?.item?._id
                                    ? "opacity-50"
                                    : ""
                                )}
                              >
                                {(updateItemOptional.isPending || togglingItemId === selectedItemData?.item?._id) && (
                                  <Loader2 className="h-3 w-3 animate-spin absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                                )}
                              </Switch>
                              <div className="flex flex-col gap-0.5">
                                <Label
                                  htmlFor={`optional-${selectedEntity?.data?._id}`}
                                  className="text-sm font-medium cursor-pointer leading-none"
                                >
                                  Optional
                                </Label>
                                <p className="text-xs text-muted-foreground">Students can skip this item</p>
                              </div>
                            </div>
                          )}
                          {selectedEntity.type === "item" && (() => {
                            const itemId = selectedItemData?.item?._id;
                            const itemOverride: DetectorSetting[] | null | undefined =
                              optimisticItemDetectors && optimisticItemDetectors.itemId === itemId
                                ? optimisticItemDetectors.detectors
                                : selectedItemData?.item?.proctoringDetectors;
                            const isOverriding = itemOverride != null;
                            const isBusy =
                              updateItemProctoring.isPending && togglingItemProctoringId === itemId;
                            const saveItemDetectors = async (detectors: DetectorSetting[] | null) => {
                              if (!versionId || !itemId) return;
                              // Always show the click immediately, even if a
                              // save is already in flight -- DetectorChecklist
                              // recomputes `value` from this on every render,
                              // so the NEXT click (guarded below) still starts
                              // from what the user just checked, not a stale
                              // pre-click snapshot.
                              setOptimisticItemDetectors({ itemId, detectors });
                              if (itemProctoringSaveInFlight.current) {
                                // A save is already running. Earlier this just
                                // returned here, silently dropping the click --
                                // confirmed live: 4 rapid checkbox clicks left
                                // only the first one persisted. Record this as
                                // the latest desired state instead; the running
                                // save's own drain loop below picks it up.
                                itemProctoringPending.current = detectors;
                                return;
                              }
                              itemProctoringSaveInFlight.current = true;
                              setTogglingItemProctoringId(itemId);
                              let toSend: DetectorSetting[] | null | undefined = detectors;
                              while (toSend !== undefined) {
                                try {
                                  await updateItemProctoring.mutateAsync({
                                    params: { path: { versionId, itemId } },
                                    body: { detectors: toSend },
                                  });
                                  // Awaited so isBusy (and the Switch's disabled
                                  // state) doesn't clear until the new value has
                                  // actually landed -- otherwise the switch
                                  // re-enables while still showing the stale
                                  // checked state, and a click in that gap fires
                                  // a second save that races the first.
                                  await refetchItem();
                                } catch (error) {
                                  toast.error('Failed to update item proctoring status');
                                }
                                // Drain whatever the newest click queued while
                                // this save was in flight; loop again if one
                                // landed, otherwise stop.
                                toSend = itemProctoringPending.current;
                                itemProctoringPending.current = undefined;
                              }
                              // Clears the optimistic value either way: on
                              // success the last refetch already has the real
                              // value, on failure this snaps back to whatever
                              // selectedItemData still says.
                              setOptimisticItemDetectors(null);
                              setTogglingItemProctoringId(null);
                              itemProctoringSaveInFlight.current = false;
                            };
                            return (
                              <div className="flex flex-col gap-2 px-3 py-2 rounded-md border bg-card">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex flex-col gap-0.5">
                                    <Label className="text-sm font-medium leading-none">
                                      Proctoring
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                      {isOverriding
                                        ? "Custom detectors for this item"
                                        : "Inherits the module/course setting"}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={isOverriding}
                                      onCheckedChange={checked =>
                                        saveItemDetectors(checked ? allDetectorsOff() : null)
                                      }
                                    />
                                    {isBusy && <Loader2 className="h-3 w-3 animate-spin" />}
                                  </div>
                                </div>
                                {isOverriding && (
                                  <>
                                    {/* Not disabled while isBusy: TanStack's
                                    isPending flickers false between each
                                    drain-loop iteration (during the await
                                    refetchItem() gap), which would let a
                                    disabled Radix control silently swallow
                                    clicks at the browser level before they
                                    ever reach the ref-guard/pending queue
                                    below that's built to handle exactly this
                                    concurrency. Confirmed live: this caused
                                    every other rapid click to be lost. */}
                                    <DetectorChecklist
                                      value={itemOverride ?? allDetectorsOff()}
                                      onChange={next => saveItemDetectors(next)}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      Leave all unchecked to disable proctoring entirely for this item.
                                    </p>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                          {selectedEntity.type === "module" && (() => {
                            const moduleId = selectedEntity.data?.moduleId;
                            const moduleOverride: DetectorSetting[] | null | undefined =
                              optimisticModuleDetectors && optimisticModuleDetectors.moduleId === moduleId
                                ? optimisticModuleDetectors.detectors
                                : selectedEntity.data?.proctoringDetectors;
                            const isOverriding = moduleOverride != null;
                            const isBusy =
                              updateModuleProctoring.isPending && togglingModuleProctoringId === moduleId;
                            const saveModuleDetectors = async (detectors: DetectorSetting[] | null) => {
                              if (!versionId || !moduleId) return;
                              // Always show the click immediately, even if a
                              // save is already in flight -- DetectorChecklist
                              // recomputes `value` from this on every render,
                              // so the NEXT click (guarded below) still starts
                              // from what the user just checked, not a stale
                              // pre-click snapshot.
                              setOptimisticModuleDetectors({ moduleId, detectors });
                              if (moduleProctoringSaveInFlight.current) {
                                // A save is already running. Earlier this just
                                // returned here, silently dropping the click --
                                // confirmed live: 4 rapid checkbox clicks left
                                // only the first one persisted. Record this as
                                // the latest desired state instead; the running
                                // save's own drain loop below picks it up.
                                moduleProctoringPending.current = detectors;
                                return;
                              }
                              moduleProctoringSaveInFlight.current = true;
                              setTogglingModuleProctoringId(moduleId);
                              let toSend: DetectorSetting[] | null | undefined = detectors;
                              while (toSend !== undefined) {
                                const sending = toSend;
                                try {
                                  await updateModuleProctoring.mutateAsync({
                                    params: { path: { versionId, moduleId } },
                                    body: { detectors: sending },
                                  });
                                  // selectedEntity.data is a plain snapshot taken at selection
                                  // time, not a live query result like selectedItemData --
                                  // refetchVersion() alone updates initialModules (the tree)
                                  // but not this detail panel, so it would keep showing the
                                  // stale value until the module was re-selected. Update it
                                  // directly.
                                  setSelectedEntity((prev: any) =>
                                    prev?.type === "module" && prev.data?.moduleId === moduleId
                                      ? { type: "module", data: { ...prev.data, proctoringDetectors: sending ?? undefined } }
                                      : prev
                                  );
                                  await refetchVersion();
                                } catch (error) {
                                  toast.error('Failed to update module proctoring status');
                                }
                                // Drain whatever the newest click queued while
                                // this save was in flight; loop again if one
                                // landed, otherwise stop.
                                toSend = moduleProctoringPending.current;
                                moduleProctoringPending.current = undefined;
                              }
                              // On failure this drops back to selectedEntity.data's
                              // original value since it was never overwritten above.
                              setOptimisticModuleDetectors(null);
                              setTogglingModuleProctoringId(null);
                              moduleProctoringSaveInFlight.current = false;
                            };
                            return (
                              <div className="flex flex-col gap-2 px-3 py-2 rounded-md border bg-card">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex flex-col gap-0.5">
                                    <Label className="text-sm font-medium leading-none">
                                      Proctoring
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                      {isOverriding
                                        ? "Custom detectors for items in this module"
                                        : "Inherits the course's universal setting"}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={isOverriding}
                                      onCheckedChange={checked =>
                                        saveModuleDetectors(checked ? allDetectorsOff() : null)
                                      }
                                    />
                                    {isBusy && <Loader2 className="h-3 w-3 animate-spin" />}
                                  </div>
                                </div>
                                {isOverriding && (
                                  <>
                                    {/* Not disabled while isBusy -- see the
                                    matching comment on the item panel above. */}
                                    <DetectorChecklist
                                      value={moduleOverride ?? allDetectorsOff()}
                                      onChange={next => saveModuleDetectors(next)}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                      Leave all unchecked to disable proctoring entirely for items in this module.
                                    </p>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                          {/* <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600">
                          {selectedEntity.type.charAt(0).toUpperCase() + selectedEntity.type.slice(1)}
                        </Badge> */}
                        </div>
                      </div>
                      <div className="text-sm text-slate-500 dark:text-gray-400 flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        <span className="flex items-center gap-1">
                          Course
                          <ChevronRight size={16} />
                          Module
                          <ChevronRight size={16} />
                          {selectedEntity.type}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-3">
                      {(selectedEntity.type !== "item") && (
                        <>
                          <Label className="text-sm font-bold text-foreground">Title *</Label>
                          <Input
                            value={
                              selectedEntity.type === "item"
                                ? selectedItemData?.item?.name ?? ""
                                : selectedEntity.data?.name ?? ""
                            }
                            disabled={
                              (selectedEntity.type === "module" && !isEditingModule) ||
                              (selectedEntity.type === "section" && !isEditingSection)
                            }
                            onChange={e => {
                              const value = e.target.value;
                              setSelectedEntity({
                                ...selectedEntity,
                                data: { ...selectedEntity.data, name: value }
                              })
                              if (selectedEntity.type === "module") {
                                if (!value.trim()) {
                                  setErrors(errors => ({ ...errors, title: "Module name is required." }));
                                } else {
                                  setErrors(errors => ({ ...errors, title: "" }));
                                }
                              }
                              if (selectedEntity.type === "section") {
                                if (!value.trim()) {
                                  setErrors(errors => ({ ...errors, title: "Section name is required." }));
                                } else {
                                  setErrors(errors => ({ ...errors, title: "" }));
                                }
                              }
                            }
                            }
                          />
                          {errors.title && (
                            <div className="text-xs text-red-500">{errors.title}</div>
                          )}
                        </>
                      )}

                      {(selectedEntity.type === "module" || selectedEntity.type === "section") && (
                        <div className="flex gap-6 text-xs text-muted-foreground">
                          <div>
                            <span className="font-semibold">Created:</span>{" "}
                            {selectedEntity.data?.createdAt
                              ? new Date(selectedEntity.data.createdAt).toLocaleString()
                              : "N/A"}
                          </div>
                          <div>
                            <span className="font-semibold">Updated:</span>{" "}
                            {selectedEntity.data?.updatedAt
                              ? new Date(selectedEntity.data.updatedAt).toLocaleString()
                              : "N/A"}
                          </div>
                        </div>
                      )}

                      {selectedEntity.type === "item" && (
                        <div className="flex gap-6 text-xs text-muted-foreground">
                          <div>
                            <span className="font-semibold">Created:</span>{" "}
                            {(() => {
                              const createdAt = getCreatedAtFromObjectId(selectedItemData?.item?._id);
                              return createdAt ? createdAt.toLocaleString() : "N/A";
                            })()}
                          </div>
                        </div>
                      )}

                      {(selectedEntity.type !== "item") && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-sm font-bold text-foreground">Description *</Label>
                            <div className="relative">
                              <textarea
                                value={
                                  selectedEntity.type === "item"
                                    ? selectedItemData?.item?.description ?? ""
                                    : selectedEntity.data?.description ?? ""
                                }
                                disabled={
                                  (selectedEntity.type === "module" && !isEditingModule) ||
                                  (selectedEntity.type === "section" && !isEditingSection)
                                }
                                onChange={e => {
                                  const value = e.target.value;

                                  // Only update if within limit or deleting characters
                                  if (value.length <= MAX_DESCRIPTION_LENGTH) {
                                    setSelectedEntity({
                                      ...selectedEntity,
                                      data: { ...selectedEntity.data, description: value }
                                    });
                                  }

                                  // Validation
                                  if (selectedEntity.type === "module") {
                                    if (!value.trim()) {
                                      setErrors(errors => ({ ...errors, description: "Module description is required." }));
                                    } else if (value.length >= MAX_DESCRIPTION_LENGTH) {
                                      setErrors(errors => ({ ...errors, description: `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less.` }));
                                    } else {
                                      setErrors(errors => ({ ...errors, description: "" }));
                                    }
                                  }
                                  if (selectedEntity.type === "section") {
                                    if (!value.trim()) {
                                      setErrors(errors => ({ ...errors, description: "Section description is required." }));
                                    } else if (value.length >= MAX_DESCRIPTION_LENGTH) {
                                      setErrors(errors => ({ ...errors, description: `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less.` }));
                                    } else {
                                      setErrors(errors => ({ ...errors, description: "" }));
                                    }
                                  }
                                }}
                                placeholder={`Description (max ${MAX_DESCRIPTION_LENGTH} characters)`}
                                rows={5}
                                maxLength={MAX_DESCRIPTION_LENGTH}
                                className={`w-full rounded border px-3 py-2 pr-16 text-sm ${(selectedEntity.type === "module" && !isEditingModule) ||
                                  (selectedEntity.type === "section" && !isEditingSection)
                                  ? 'bg-muted/50 border-transparent'
                                  : ''
                                  }`}
                              />
                              <div className={`absolute bottom-2 right-2 text-xs ${(selectedEntity.data?.description?.length || 0) >= (MAX_DESCRIPTION_LENGTH * 0.9)
                                ? 'text-destructive'
                                : 'text-muted-foreground'
                                }`}>
                                {selectedEntity.data?.description?.length || 0}/{MAX_DESCRIPTION_LENGTH}
                              </div>
                            </div>
                            {errors.description && (
                              <div className="text-xs text-red-500">{errors.description}</div>
                            )}
                          </div>
                        </>
                      )}
                      <div className="flex items-center gap-2">
                        {(selectedEntity.type === "module" || selectedEntity.type === "section") &&
                          (isEditingModule || isEditingSection) &&
                          (
                            <Button
                              onClick={() => {
                                const moduleName = selectedEntity.data.name?.trim();
                                const moduleDescription = selectedEntity.data.description?.trim() ?? "";
                                const sectionName = selectedEntity.data.name?.trim();
                                const sectionDescription = selectedEntity.data.description?.trim() ?? "";
                                if (selectedEntity.type === "module") {
                                  if (!isEditingModule) {
                                    setIsEditingModule(true);
                                    setOriginalModuleData({
                                      name: selectedEntity.data.name,
                                      description: selectedEntity.data.description || ""
                                    });
                                    return;
                                  }

                                  const moduleName = selectedEntity.data.name?.trim();
                                  const moduleDescription = selectedEntity.data.description?.trim() ?? "";
                                  if (!moduleName || !moduleDescription) {
                                    setErrors({
                                      title: !moduleName ? "Module name is required." : "",
                                      description: !moduleDescription
                                        ? "Module description is required."
                                        : moduleDescription.length >= MAX_DESCRIPTION_LENGTH
                                          ? `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less.`
                                          : ""
                                    });
                                    return;
                                  }

                                  setErrors({ title: "", description: "" });
                                  if (versionId) {
                                    updateModuleAsync({
                                      params: { path: { versionId, moduleId: selectedEntity.data.moduleId } },
                                      body: {
                                        name: selectedEntity.data.name,
                                        description: selectedEntity.data.description || ""
                                      }
                                    }).then((res) => {
                                      refetchVersion();
                                      if (shouldFetchItems) {
                                        refetchItems();
                                      }
                                      setIsEditingModule(false);
                                    });
                                  }
                                  return;
                                }

                                if (selectedEntity.type === "section") {
                                  if (!isEditingSection) {
                                    setIsEditingSection(true);
                                    setOriginalSectionData({
                                      name: selectedEntity.data.name,
                                      description: selectedEntity.data.description || ""
                                    });
                                    return;
                                  }
                                  const sectionName = selectedEntity.data.name?.trim();
                                  const sectionDescription = selectedEntity.data.description?.trim() ?? "";

                                  if (!sectionName || !sectionDescription) {
                                    setErrors({
                                      title: !sectionName ? "Section name is required." : "",
                                      description: !sectionDescription
                                        ? "Section description is required."
                                        : sectionDescription.length >= MAX_DESCRIPTION_LENGTH
                                          ? `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less.`
                                          : ""
                                    });
                                    return;
                                  }
                                }
                                setErrors({ title: "", description: "" });
                                if (selectedEntity.type === "section" && versionId && selectedEntity.parentIds?.moduleId) {
                                  updateSectionAsync({
                                    params: {
                                      path: {
                                        versionId,
                                        moduleId: selectedEntity.parentIds.moduleId,
                                        sectionId: selectedEntity.data.sectionId
                                      }
                                    },
                                    body: {
                                      name: selectedEntity.data.name,
                                      description: selectedEntity.data.description || ""
                                    }
                                  }).then((res) => {
                                    refetchVersion();
                                    if (shouldFetchItems) {
                                      refetchItems();
                                    }
                                    setIsEditingSection(false);
                                  });
                                }
                                if (selectedEntity.type === "item" && versionId && selectedEntity.parentIds?.moduleId && selectedEntity.parentIds?.sectionId) {
                                  updateItemAsync({
                                    params: {
                                      path: {
                                        versionId,
                                        moduleId: selectedEntity.parentIds.moduleId,
                                        sectionId: selectedEntity.parentIds.sectionId,
                                        itemId: selectedEntity.data._id
                                      }
                                    },
                                    body: {
                                      name: selectedEntity.data.name,
                                      description: selectedEntity.data.description || ""
                                    }
                                  }).then((res) => {
                                    refetchVersion();
                                    refetchItems(); refetchItem()
                                  });
                                }
                              }}
                              variant={isEditingModule || isEditingSection ? "default" : "ghost"}
                              size={isEditingModule || isEditingSection ? "default" : "icon"}
                              className={isEditingModule || isEditingSection ?
                                "bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 transition-all duration-300" : "h-8 w-8"}
                            >
                              {isEditingModule || isEditingSection ? (
                                'Save Changes'
                              ) : (
                                <Pencil className="h-4 w-4" />
                              )}
                              <span className="sr-only">Edit {selectedEntity.type}</span>
                            </Button>
                          )}

                        {((selectedEntity.type === "module" && isEditingModule) || (selectedEntity.type === "section" && isEditingSection)) && (
                          <Button
                            variant="outline"
                            className="border-border bg-background"
                            onClick={() => {
                              if (selectedEntity.type === 'module' && originalModuleData) {
                                setSelectedEntity({
                                  ...selectedEntity,
                                  data: {
                                    ...selectedEntity.data,
                                    name: originalModuleData.name,
                                    description: originalModuleData.description
                                  }
                                });
                                setIsEditingModule(false);
                              } else if (selectedEntity.type === 'section' && originalSectionData) {
                                setSelectedEntity({
                                  ...selectedEntity,
                                  data: {
                                    ...selectedEntity.data,
                                    name: originalSectionData.name,
                                    description: originalSectionData.description
                                  }
                                });
                                setIsEditingSection(false);
                              }
                              setErrors({ title: "", description: "" });
                            }}
                          >
                            Cancel
                          </Button>
                        )}

                        {(selectedEntity?.type === "module" || selectedEntity?.type === "section") && (
                          <Button
                            variant="outline"
                            className="border-border bg-background"
                            onClick={() => setIsDeleteModalOpen(true)}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Delete {selectedEntity.type}
                          </Button>
                        )}
                      </div>
                      <div className="relative group">

                        <ConfirmationModal
                          isOpen={isDeleteModalOpen}
                          onClose={() => setIsDeleteModalOpen(false)}
                          onConfirm={handleConfirmDelete}
                          title={
                            selectedEntity?.type === "module"
                              ? "Delete Module"
                              : "Delete Section"
                          }
                          description={
                            selectedEntity?.type === "module"
                              ? "This will delete this module and all its sections/items. Are you sure?"
                              : "This will delete this section and all its items. Are you sure?"
                          }
                          confirmText="Delete"
                          cancelText="Cancel"
                          isDestructive
                          // isLoading={isDeleting}
                          loadingText="Deleting..."
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      </div>



                      {/* {selectedEntity.type === "item" && selectedEntity.data.type === "VIDEO" && (
                        <>
                        
                          <VideoModal
                            isLoading={isItemLoading}
                            selectedItemName={selectedItem.name}
                            action={isEditingItem ? "edit" : "view"}
                            item={selectedItemData?.item}
                            courseId={courseId}
                            courseVersionId={versionId}
                            onClose={() => setIsEditingItem(false)}
                            onSave={video => {
                              const formattedVideo = {
                                ...video,
                                type: "VIDEO",
                                details: {
                                  ...video.details,
                                  startTime: video.details.startTime,
                                  endTime: video.details.endTime,
                                }
                              };
                              if (
                                selectedEntity.parentIds?.moduleId &&
                                selectedEntity.parentIds?.sectionId &&
                                selectedEntity.data?._id &&
                                versionId
                              ) {
                                updateVideoAsync({
                                  params: {
                                    path: {
                                      versionId,
                                      itemId: selectedEntity.data._id,
                                    }
                                  },
                                  body: formattedVideo,
                                }).then((res) => {
                                  refetchVersion();
                                  if (shouldFetchItems) {
                                    refetchItems();
                                  }
                                  refetchItem();
                                });
                                toast.success("Video details saved successfully");
                                setIsEditingItem(false);
                              }
                            }}
                            onDelete={() => {
                              if (
                                selectedEntity.parentIds?.sectionId &&
                                selectedEntity.data?._id
                              ) {

                                deleteItemAsync({
                                  params: { path: { itemsGroupId: selectedEntity.parentIds?.itemsGroupId || "", itemId: selectedEntity.data._id } }
                                }).then((res) => {
                                  refetchVersion();
                                  if (shouldFetchItems) {
                                    refetchItems();
                                  }
                                  refetchItem();
                                });
                                setSelectedEntity(null);
                                setIsEditingItem(false);

                              }
                            }}
                            onEdit={() => setIsEditingItem(true)}
                          />
                        </>
                      )} */}
                      {selectedEntity.type === "item" && selectedEntity.data.type === "VIDEO" && (
                        <Tabs value={videoTab} onValueChange={setVideoTab} className=" mb-4">

                          <TabsList className=" overflow-x-auto no-scrollbar">

                            <TabsTrigger value="video" className="flex items-center gap-2 cursor-pointer">
                              <Video className="h-4 w-4" />
                              Video
                            </TabsTrigger>

                            <TabsTrigger value="analytics" className="flex items-center gap-2 cursor-pointer">
                              <BarChart3 className="h-4 w-4" />
                              Analytics
                            </TabsTrigger>

                          </TabsList>

                          {videoTab === "video" && (
                            <VideoModal
                              isLoading={isItemLoading}
                              selectedItemName={selectedItem.name}
                              action={isEditingItem ? "edit" : "view"}
                              item={selectedItemData?.item}
                              courseId={courseId}
                              courseVersionId={versionId}
                              onClose={() => setIsEditingItem(false)}
                              onSave={video => {
                                const formattedVideo = {
                                  ...video,
                                  type: "VIDEO",
                                  details: {
                                    ...video.details,
                                    startTime: video.details.startTime,
                                    endTime: video.details.endTime,
                                  }
                                };

                                if (
                                  selectedEntity.parentIds?.moduleId &&
                                  selectedEntity.parentIds?.sectionId &&
                                  selectedEntity.data?._id &&
                                  versionId
                                ) {
                                  updateVideoAsync({
                                    params: {
                                      path: {
                                        courseId: courseId || "",
                                        versionId,
                                        itemId: selectedEntity.data._id,
                                      }
                                    },
                                    body: formattedVideo,
                                  }).then(() => {
                                    refetchVersion();
                                    shouldFetchItems && refetchItems();
                                    refetchItem();
                                  });

                                  toast.success("Video details saved successfully");
                                  setIsEditingItem(false);
                                }
                              }}

                              onDelete={() => {
                                if (
                                  selectedEntity.parentIds?.sectionId &&
                                  selectedEntity.data?._id
                                ) {
                                  deleteItemAsync({
                                    params: {
                                      path: {
                                        courseId: courseId || "",
                                        itemsGroupId: selectedEntity.parentIds?.itemsGroupId || "",
                                        itemId: selectedEntity.data._id
                                      }
                                    }
                                  }).then(() => {
                                    refetchVersion();
                                    shouldFetchItems && refetchItems();
                                    refetchItem();
                                  });

                                  setSelectedEntity(null);
                                  setIsEditingItem(false);
                                }
                              }}

                              onEdit={() => setIsEditingItem(true)}
                            />
                          )}

                          {videoTab === "analytics" && (
                            <div className="mt-4">
                              <p className="text-sm text-muted-foreground">
                                <UserAnalytics users={userAnalyticsData || []} overallAnalytics={overallAnalytics} currentPage={videoAnalyticsPage} limit={videoAnalyticsLimit} search={videoAnalyticsSearch} onSearchChange={(v) => {
                                  setVideoAnalyticsSearch(v);
                                  setVideoAnalyticsPage(1);
                                }} sortBy={videoAnalyticsSortBy} sortOrder={videoAnalyticsSortOrder} onSortChange={(field) => {
                                  if (videoAnalyticsSortBy === field) {
                                    setVideoAnalyticsSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setVideoAnalyticsSortBy(field);
                                    setVideoAnalyticsSortOrder('asc');
                                  }
                                }} onPageChange={setVideoAnalyticsPage} isLoading={overallLoading || usersLoading} totalDocuments={userAnalyticsTotalDocs || 0} totalPages={userAnalyticsTotalPages || 0} />
                              </p>
                            </div>
                          )}

                        </Tabs>
                      )}

                      {/* <CreateArticle/> */}
                      {selectedEntity.type === "item" && selectedEntity.data.type === "QUIZ" && courseId && versionId && (
                        <EnhancedQuizEditor
                          isLoading={isLoading}
                          selectedItemName={selectedItem.name}
                          quizId={selectedQuizId}
                          moduleId={selectedEntity.parentIds?.moduleId || ""}
                          sectionId={selectedEntity.parentIds?.sectionId || ""}
                          courseId={courseId}
                          courseVersionId={versionId}
                          details={quizDetails}
                          analytics={quizAnalytics}
                          // submissions={quizSubmissions}
                          performance={quizPerformance}
                          questionId={currentCourse?.questionId || null}
                          onDelete={() => {
                            deleteItemAsync({
                              params: { path: { courseId: courseId, itemsGroupId: selectedEntity.parentIds?.itemsGroupId || "", itemId: selectedQuizId } }
                            }).then((res) => {
                              refetchVersion();
                              refetchItems();
                            });
                            setSelectedEntity(null);
                          }}
                        />
                      )}
                      {selectedEntity.type === "item" && selectedEntity.data.type === "PROJECT" && courseId && versionId && (
                        <ProjectItem
                          mode="edit"
                          name={projectEditName}
                          description={projectEditDescription}
                          onNameChange={setProjectEditName}
                          onDescriptionChange={setProjectEditDescription}
                          onSave={async () => {
                            const projectId = selectedEntity.data._id;
                            const name = projectEditName;
                            const description = projectEditDescription;
                            if (projectId && versionId) {
                              try {
                                await updateCourseItemAsync({
                                  params: { path: { courseId: courseId || "", versionId, itemId: projectId } },
                                  body: { name, description, details: { name, description }, type: 'PROJECT' }
                                });
                                refetchVersion();
                                refetchItems();
                                refetchItem();
                                toast.success("Project updated successfully");
                              } catch (err) {
                                toast.error('Failed to update project: ' + (err?.message || 'Unknown error'));
                              }
                            }
                          }}
                          onDelete={async () => {
                            const projectId = selectedEntity.data._id;
                            if (selectedEntity.parentIds?.itemsGroupId && projectId) {

                              await deleteItemAsync({
                                params: { path: {courseId:courseId, itemsGroupId: selectedEntity.parentIds.itemsGroupId, itemId: projectId } },
                              });
                              refetchVersion();
                              refetchItems();
                              refetchItem();
                              setSelectedEntity(null);
                              toast.success("Project deleted successfully");

                            }
                          }}
                          onClose={() => {
                            refetchItem();
                          }}
                        />
                      )}
                      {selectedEntity.type === "item" && selectedEntity.data.type === "BLOG" && courseId && versionId && (
                        <EnhancedBlogEditor
                          isLoading={isLoading}
                          selectedItemName={selectedItem.name}
                          blogId={selectedEntity.data._id}
                          moduleId={selectedEntity.parentIds?.moduleId || ""}
                          sectionId={selectedEntity.parentIds?.sectionId || ""}
                          courseId={courseId}
                          courseVersionId={versionId}
                          details={selectedItemData}
                          onRefetch={() => {
                            refetchVersion();
                            refetchItems();
                            refetchItem();
                          }}
                          onDelete={() => {
                            deleteItemAsync({
                              params: { path: {courseId:courseId, itemsGroupId: selectedEntity.parentIds?.itemsGroupId || "", itemId: selectedEntity.data._id } }
                            }).then((res) => {
                              refetchVersion();
                              refetchItems();
                            });
                            setSelectedEntity(null);
                          }}
                        />
                      )}

                      {/* {selectedEntity.type === "item" && selectedEntity.data.type === "FEEDBACK" && (
                    
  <FeedbackFormEditor  />
)} */}


                      {selectedEntity.type === "item" && selectedEntity.data.type === "REFLECTION" && (
                        <ReflectionItemEditor
                          itemId={selectedEntity.data._id}
                          courseId={courseId!}
                          versionId={versionId!}
                          name={selectedEntity.data.name}
                          description={selectedEntity.data.description}
                          details={(selectedItemData as any)?.details}
                          onSaved={() => {
                            refetchVersion();
                            refetchItems();
                            refetchItem();
                          }}
                          onDelete={() => {
                            deleteItemAsync({
                              params: { path: { courseId: courseId || "", itemsGroupId: selectedEntity.parentIds?.itemsGroupId || "", itemId: selectedEntity.data._id } }
                            }).then(() => {
                              refetchVersion();
                              refetchItems();
                            });
                            setSelectedEntity(null);
                          }}
                        />
                      )}

                      {selectedEntity.type === "item" && selectedEntity.data.type === "CASE_STUDY" && (
                        <Tabs value={caseStudyTab} onValueChange={setCaseStudyTab} className="mb-4">
                          <TabsList>
                            <TabsTrigger value="settings">Settings</TabsTrigger>
                            <TabsTrigger value="responses">Responses</TabsTrigger>
                          </TabsList>
                          {caseStudyTab === "settings" && (
                            <CaseStudyItemEditor
                              itemId={selectedEntity.data._id}
                              courseId={courseId!}
                              versionId={versionId!}
                              name={selectedEntity.data.name}
                              description={selectedEntity.data.description}
                              details={(selectedItemData as any)?.details}
                              onSaved={() => {
                                refetchVersion();
                                refetchItems();
                                refetchItem();
                              }}
                            />
                          )}
                          {caseStudyTab === "responses" && (
                            <CaseStudyResponsesPanel
                              courseId={courseId!}
                              versionId={versionId!}
                              itemId={selectedEntity.data._id}
                            />
                          )}
                        </Tabs>
                      )}

                      {selectedEntity.type === "item" && selectedEntity.data.type === "FEEDBACK" && (
                        <FeedbackFormEditor
                          isLoading={isLoading}
                          selectedItemName={selectedItem.name}
                          feedbackId={selectedEntity.data._id}
                          moduleId={selectedEntity.parentIds?.moduleId || ""}
                          sectionId={selectedEntity.parentIds?.sectionId || ""}
                          courseId={courseId!}
                          courseVersionId={versionId!}
                          details={selectedItemData}
                          onRefetch={() => {
                            refetchVersion();
                            refetchItems();
                            refetchItem();
                          }}
                          onDelete={() => {
                            deleteItemAsync({
                              params: { path: {courseId: courseId || "", itemsGroupId: selectedEntity.parentIds?.itemsGroupId || "", itemId: selectedEntity.data._id } }
                            }).then(() => {
                              refetchVersion();
                              refetchItems();
                            });
                            setSelectedEntity(null);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                    <div className="flex flex-col items-center justify-center h-[80vh] text-center relative -mt-16">
                      {/* Animated Glow */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0">
                        <div className="w-60 h-60 rounded-full bg-gradient-to-br from-primary/20 via-accent/10 to-primary/20 blur-3xl opacity-70 animate-pulse"></div>
                      </div>

                      {/* Animated Icon */}
                      <div className="relative z-10 mb-6">
                        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary/10 to-accent/10 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center shadow-lg animate-float">
                          <BookOpen className="h-16 w-16 text-primary dark:text-primary drop-shadow-lg" />
                        </div>
                      </div>

                      {/* Course & Version Info */}
                    <div className="relative z-10 mb-6 w-full max-w-2xl px-4">
                      <div className="bg-muted/40 border rounded-xl px-6 py-5 flex gap-6 items-stretch">
                        {/* Course */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-1">Course</p>
                          <h2 className="text-lg font-bold text-foreground leading-snug line-clamp-2" title={courseData?.name || 'Untitled Course'}>
                            {isLoading ? <span className="inline-block h-5 w-48 bg-muted rounded animate-pulse" /> : courseData?.name || 'Untitled Course'}
                          </h2>
                          {courseData?.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{courseData.description}</p>
                          )}
                        </div>

                        {/* Divider */}
                        <div className="w-px self-stretch bg-border" />

                        {/* Version */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-1">Version</p>
                          <h3 className="text-base font-semibold text-foreground leading-snug line-clamp-2" title={(versionData as any)?.version || (versionData as any)?.name || 'Unknown Version'}>
                            {isLoading ? <span className="inline-block h-4 w-36 bg-muted rounded animate-pulse" /> : (versionData as any)?.version || (versionData as any)?.name || 'Unknown Version'}
                          </h3>
                          {(versionData as any)?.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{(versionData as any).description}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="relative z-10 mb-6 w-full max-w-2xl px-4">
                      <div className="rounded-xl border bg-background/80 px-5 py-4 text-left">
                        {isLoading ? (
                          <p className="text-sm text-muted-foreground">Loading course editor data...</p>
                        ) : modules.length > 0 ? (
                          <>
                            <p className="text-sm font-medium text-foreground">
                              Editor is ready.
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Select a module, section, or item from the left panel to view the full edit card.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-foreground">
                              No modules yet.
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Add your first module to unlock all course editing components.
                            </p>
                          </>
                        )}
                        <p className={`text-xs mt-3 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'} text-muted-foreground`}>
                          {displayedMessage}
                        </p>
                      </div>
                    </div>

                      {/* CTA Button */}
                      {!isLoading && modules.length === 0 && (
                        <Button
                          onClick={handleAddModule}
                          className="relative z-10 bg-gradient-to-r from-primary to-accent hover:from-accent hover:to-primary text-primary-foreground font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-sm md:text-base flex items-center gap-3 animate-bounce-slow group"
                        >
                          <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                          Add new module
                        </Button>
                      )}

                      {/* ViBe Features */}
                      <div className="relative z-10 mt-8 md:flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400">
                        <div className="flex items-center gap-2 mb-2 md:mb-0">
                          <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                          <span>AI-Powered Content</span>
                        </div>
                        <div className="flex items-center gap-2 mb-2 md:mb-0">
                          <div className="w-2 h-2 bg-accent rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                          <span>Smart Course Builder</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                          <span>Interactive Learning</span>
                        </div>
                      </div>
                    </div>

              ))}


          </div>
        </SidebarInset>
      </ResizablePanel>

      {/* Add Video Modal */}
      {showAddVideoModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.25)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(6px)", // <-- add blur effect
            WebkitBackdropFilter: "blur(6px)", // for Safari support
          }}
          className="overflow-y-scroll"
        >
          <VideoModal
            isLoading={isLoading}
            selectedItemName={selectedItem.name}
            action="add"
            courseId={courseId}
            courseVersionId={versionId}
            onClose={() => setShowAddVideoModal(null)}
            onSave={video => {
              handleAddItem(
                showAddVideoModal.moduleId,
                showAddVideoModal.sectionId,
                "VIDEO",
                video
              );
              setShowAddVideoModal(null);
            }}
          />
        </div>
      )}


      {/* Add Quiz Modal */}
      <QuizWizardModal
        quizWizardOpen={quizWizardOpen}
        setQuizWizardOpen={setQuizWizardOpen}
      />

      {studentQuestionSegment && courseId && versionId && (
        <StudentQuestionPanel
          isOpen={studentQuestionSegment !== null}
          onOpenChange={(open) => {
            if (!open) setStudentQuestionSegment(null);
          }}
          courseId={courseId}
          courseVersionId={versionId}
          segmentId={studentQuestionSegment.segmentId}
          segmentTitle={studentQuestionSegment.segmentTitle}
        />
      )}

    </ResizablePanelGroup>
  );
}

export default function TeacherCoursePage() {
  const [initialScreenSize, setInitialScreenSize] = useState<boolean | null>(null);

  useEffect(() => {
    const width = window.innerWidth;
    setInitialScreenSize(width >= 768);
  }, []);

  return (
    <SidebarProvider defaultOpen={initialScreenSize ?? true}>
      <TeacherCourseContent />
    </SidebarProvider>
  );
}


type SuccessFlagEntry = {
  flag: boolean;
  message: string;
};

type ErrorFlagEntry = {
  flag: boolean;
  message?: string;
  fallback: string;
};

export function useStatusToasts({
  successFlags,
  errorFlags,
}: {
  successFlags: Record<string, SuccessFlagEntry>;
  errorFlags: Record<string, ErrorFlagEntry>;
}) {
  const prevSuccess = useRef<Record<string, boolean>>({});
  const prevError = useRef<Record<string, boolean>>({});

  useEffect(() => {
    // Success toasts
    Object.entries(successFlags).forEach(([key, { flag, message }]) => {
      const wasPrev = prevSuccess.current[key];
      if (!wasPrev && flag) {
        toast.success(message);
      }
      prevSuccess.current[key] = flag;
    });

    // Error toasts
    Object.entries(errorFlags).forEach(([key, { flag, message, fallback }]) => {
      const wasPrev = prevError.current[key];
      if (!wasPrev && flag) {
        toast.error(fallback, {
          description: message ?? "An unknown error occurred.",
        });
      }
      prevError.current[key] = flag;
    });
  }, [successFlags, errorFlags]);
}

// 4. ADD A SIMPLE FEEDBACK EDITOR COMPONENT (Hello World for now)

export interface VideoUserAnalytics {
  firstName: string;
  email: string;
  userId: string;
  viewCount: number;
  totalWatchTime: number;
}

export interface VideoUserAnalyticsResponse {
  data: VideoUserAnalytics[];
  totalDocuments: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface VideoOverallAnalytics {
  videoId: string;
  videoDuration: number | string;
  totalViews: number;
  totalWatchHours: number;
  averageViewsPerUser: number;
  averageWatchHoursPerUser: number;
}

export type UserAnalyticsProps = {
  users: VideoUserAnalytics[] | null;
  overallAnalytics?: VideoOverallAnalytics | null;

  search: string;
  onSearchChange: (value: string) => void;

  currentPage: number;
  limit: number;
  totalDocuments: number;
  totalPages: number;
  onPageChange: (page: number) => void;

  sortBy: 'name' | 'views' | 'watchHours';
  sortOrder: 'asc' | 'desc';
  onSortChange: (field: 'name' | 'views' | 'watchHours') => void;

  isLoading?: boolean;
};

export function UserAnalytics({
  users,
  overallAnalytics,
  search,
  onSearchChange,
  currentPage,
  limit,
  totalDocuments,
  totalPages,
  onPageChange,
  sortBy,
  sortOrder,
  onSortChange,
  isLoading,
}: UserAnalyticsProps) {
  const safeUsers = users ?? [];

  const totalViewsOnPage = useMemo(
    () => safeUsers.reduce((sum, u) => sum + (u.viewCount || 0), 0),
    [safeUsers]
  );

  const totalWatchHoursOnPage = useMemo(
    () => safeUsers.reduce((sum, u) => sum + (u.watchHours || 0), 0),
    [safeUsers]
  );

  const avgViewsPerUserOnPage = safeUsers.length ? totalViewsOnPage / safeUsers.length : 0;



  return (
    <div className="w-full space-y-4 p-4">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">User Analytics</h1>
        <p className="text-sm text-muted-foreground">Per-student engagement for this video.</p>
      </div>

      {/* Overall Analytics (compact) */}
      {overallAnalytics && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">

          {/* Duration */}
          <Card className="bg-blue-50 border-blue-100">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-blue-900">
                Duration
              </CardTitle>
              <Clock className="h-4 w-4 text-blue-700" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-lg font-semibold text-blue-950">
                {overallAnalytics.videoDuration}s
              </div>
            </CardContent>
          </Card>

          {/* Total Views */}
          <Card className="bg-green-50 border-green-100">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-green-900">
                Total Views
              </CardTitle>
              <Eye className="h-4 w-4 text-green-700" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-lg font-semibold text-green-950">
                {overallAnalytics.totalViews.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          {/* Watch Hours */}
          <Card className="bg-purple-50 border-purple-100">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-purple-900">
                Watch Hours
              </CardTitle>
              <PlayCircle className="h-4 w-4 text-purple-700" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-lg font-semibold text-purple-950">
                {overallAnalytics.totalWatchHours?.toFixed(1)}
              </div>
            </CardContent>
          </Card>

          {/* Avg Watch / User */}
          <Card className="bg-orange-50 border-orange-100">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-orange-900">
                Avg Watch / User
              </CardTitle>
              <Users className="h-4 w-4 text-orange-700" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-lg font-semibold text-orange-950">
                {overallAnalytics.averageWatchHoursPerUser?.toFixed(1)}h
              </div>
            </CardContent>
          </Card>

        </div>
      )}

      {/* Search + Stats */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
          <div className="w-full sm:max-w-sm relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />

            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by name or email..."
              className="h-9 pl-9"
            />
          </div>


        </div>


      </div>

      {/* Table */}
      <Card className="bg-muted/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Users</CardTitle>
          <CardDescription className="text-xs">
            Showing{" "}
            {totalDocuments === 0 ? 0 : (currentPage - 1) * limit + 1}–
            {Math.min(currentPage * limit, totalDocuments)} of {totalDocuments}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="overflow-x-auto min-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow className="border-border bg-muted/30">
                  {[
                    { key: "name", label: "Student", className: "pl-6 w-[320px]", sortable: true },
                    { key: "views", label: "Views", className: "w-[120px] text-right", sortable: true },
                    { key: "watchHours", label: "Watch (hrs)", className: "w-[160px] text-right", sortable: true },
                    { key: "engagement", label: "Engagement", className: "w-[160px] text-center", sortable: false },
                  ].map(({ key, label, className, sortable }) => (
                    <TableHead
                      key={key}
                      className={`font-bold text-foreground select-none ${sortable ? 'cursor-pointer' : ''} ${className}`}
                      onClick={() => sortable && onSortChange(key as 'name' | 'views' | 'watchHours')}
                    >
                      <span className="flex items-center gap-1">
                        {label}
                        {sortable && sortBy === key && (
                          sortOrder === 'asc' ? (
                            <ArrowUp size={16} className="text-foreground" />
                          ) : (
                            <ArrowDown size={16} className="text-foreground" />
                          )
                        )}
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* Loading state */}
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={4} className="py-16 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        <span className="text-muted-foreground">
                          Loading user analytics…
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Empty state */}
                {!isLoading && safeUsers.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      No users found for the current search.
                    </TableCell>
                  </TableRow>
                )}

                {/* Data rows */}
                {!isLoading &&
                  safeUsers.map((user) => {
                    const engagement =
                      user.viewCount > avgViewsPerUserOnPage
                        ? "high"
                        : user.viewCount > avgViewsPerUserOnPage * 0.5
                          ? "medium"
                          : "low";

                    const badgeClass = {
                      high: "bg-primary/15 text-primary",
                      medium: "bg-muted text-foreground",
                      low: "bg-muted/60 text-muted-foreground",
                    }[engagement];

                    return (
                      <TableRow
                        key={user.userId}
                        className="border-b border-border/60 hover:bg-muted/30"
                      >
                        {/* Name + Email (common pattern) */}
                        <TableCell className="pl-6">
                          <div className="flex items-center gap-3">
                            {/* Avatar */}
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                              {user.firstName?.charAt(0)?.toUpperCase()}
                            </div>

                            {/* Name + Email */}
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground leading-tight">
                                {user.firstName}
                              </span>
                              <span className="text-xs text-muted-foreground leading-tight">
                                {user.email}
                              </span>
                            </div>
                          </div>
                        </TableCell>


                        <TableCell className="w-[120px] text-left font-medium tabular-nums">
                          {user.viewCount.toLocaleString()}
                        </TableCell>

                        <TableCell className="w-[120px] text-left font-medium tabular-nums">
                          {user.totalWatchTime}
                        </TableCell>

                        <TableCell className="text-left">
                          <Badge className={badgeClass}>
                            {engagement.charAt(0).toUpperCase() + engagement.slice(1)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>



          {/* Pagination (buttons should use bg-primary inside your Pagination component) */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalDocuments={totalDocuments}
            onPageChange={onPageChange}
            className="mt-4"
          />
        </CardContent>
      </Card>
    </div>
  );
}








